import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import registrationService from '../services/registrationService';
import masterService from '../services/masterService';
import registrationConfigService from '../services/registrationConfigService';
import patientDocumentService from '../services/patientDocumentService';
import lookupService from '../services/lookupService';
import profileService from '../services/profileService';
import packageService from '../services/packageService';
import partyService from '../services/partyService';
import { ReceiptModal } from '../components/ReceiptModal';
import receiptService from '../services/receiptService';
import { runTrfPrint, describeTrfOutcome } from '../utils/trfPrint';
import CollectionChargePanel from '../components/CollectionChargePanel';
import CascadingLocationSelector from '../components/location/CascadingLocationSelector';
import { referralService } from '../services/referralService';
import collectionBoyService from '../services/collectionBoyService';
import discountService from '../services/discountService';
import { FILE_BASE_URL } from '../services/api';
import PatientCameraModal from '../components/common/PatientCameraModal';
import FaceBiometricModal from '../components/biometric/FaceBiometricModal';
import faceAuthService from '../services/biometric/faceAuthService';
import faceApiService from '../services/biometric/faceApiService';
import draftService from '../services/draftService';
import b2bService from '../services/b2bService';
import carecoinsService from '../services/carecoinsService';
import appointmentService from '../services/appointmentService';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildRegistrationPrefill, canRegister as canRegisterAppointment, parseAppointmentIdParam } from '../utils/appointmentLogic';
import { catalogItemFromInvestigation } from '../utils/appointmentExtras';
import { isCreditBill } from '../utils/billCredit';
import './RegistrationPage.css';
import './appointments/AppointmentPage.css';

export const getAgeValidationError = (val) => {
  if (val === '' || val === null || val === undefined) return null;
  const strVal = String(val).trim();
  if (strVal === '') return null;

  if (strVal.includes('.') || strVal.includes(',')) {
    return 'Please enter a valid age. Age must be a whole number of years.';
  }

  const num = Number(strVal);
  if (isNaN(num) || !Number.isInteger(num)) {
    return 'Please enter a valid age. Age must be a whole number of years.';
  }
  if (num < 0) {
    return 'Please enter a valid age. Age cannot be negative.';
  }
  if (num > 120) {
    return 'Are you sure you entered the correct age? Maximum allowed age is 120 years.';
  }
  return null;
};

export const RegistrationPage = () => {
  const { user } = useAuth();

  // =========================================================================
  // 1. PRIMARY TWO-TAB WORKBENCH STATE
  // =========================================================================
  // 'patient_clinical' (Tab 1) | 'orders_billing' (Tab 2)
  const [, setActiveTab] = useState('patient_clinical');

  // =========================================================================
  // 2. TOAST NOTIFICATIONS
  // =========================================================================
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // =========================================================================
  // 3. DYNAMIC LOOKUPS & MASTERS (NO HARDCODED STATIC ARRAYS)
  // =========================================================================
  const [lookups, setLookups] = useState({
    titles: [],
    genders: [],
    bloodGroups: [],
    nationalities: [],
    maritalStatuses: [],
    patientCategories: [],
    visitTypes: [],
    patientTypes: [],
    identifierTypes: [],
    documentTypes: [],
    paymentModes: [],
    centres: [],
    doctors: [],
    panels: [],
  });
  const [lookupsLoading, setLookupsLoading] = useState(true);

  // Active Scope
  // Active Scope & Mandatory Master Bindings
  const [centreId, setCentreId] = useState(user?.centreId || 1);
  const [panelId, setPanelId] = useState(1);
  const [doctorId, setDoctorId] = useState(1);
  const [doctorName, setDoctorName] = useState('SELF / DIRECT REFERRAL');
  const [referringLab, setReferringLab] = useState('');
  
  // Referral Source & Beneficiary (Master-driven)
  const [referralSources, setReferralSources] = useState([]);
  const [referralSourceId, setReferralSourceId] = useState('');
  const [referralSourceName, setReferralSourceName] = useState('');
  const [beneficiaryType, setBeneficiaryType] = useState('Doctor');
  const [beneficiaryId, setBeneficiaryId] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [proName, setProName] = useState('');
  const [phlebotomistName, setPhlebotomistName] = useState('');
  const [visitType, setVisitType] = useState('Center Visit');
  const [patientType, setPatientType] = useState('OPD (Out-Patient)');
  const [srfId, setSrfId] = useState('');
  const [collectionDate, setCollectionDate] = useState(new Date().toISOString().split('T')[0]);
  const [collectionTime, setCollectionTime] = useState(new Date().toTimeString().slice(0, 5));
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);

  // B2B Corporate Client / Party Governance (REQ-B2B-01, REQ-B2B-02, REQ-B2B-03)
  const [eligibleParties, setEligibleParties] = useState([]);
  const [partyId, setPartyId] = useState('');
  const [contractId, setContractId] = useState('');
  const [clientOrderNumber, setClientOrderNumber] = useState('');
  const [partyCreditStatus, setPartyCreditStatus] = useState(null);
  const [partyGate, setPartyGate] = useState(null);   // the client's credit terms applied to this bill

  useEffect(() => {
    b2bService.getEligibleParties(centreId)
      .then((res) => setEligibleParties(res || []))
      .catch((err) => console.error('Failed to load eligible B2B parties:', err));
  }, [centreId]);

  const handleBillingSponsorChange = async (selectedValue) => {
    if (!selectedValue) return;

    if (selectedValue.startsWith('PARTY_')) {
      const pId = selectedValue.replace('PARTY_', '');
      setPartyId(pId);
      setPartyCreditStatus(null);
      if (paymentsList.length > 0) showToast('Payments entered earlier were removed: a client\'s bill goes on credit.', 'success');
      setPaymentsList([]);
      setCurrentPaymentMode('');
      setCurrentPayAmount('');

      // Find party in eligibleParties to resolve default panel ID if available
      const partyObj = eligibleParties.find((p) => String(p.partyID) === String(pId));
      const rawDefaultPanel = partyObj?.defaultPanelID ?? partyObj?.defaultPanelId;
      const defPanelId = rawDefaultPanel ? Number(rawDefaultPanel) : 1;

      try {
        const contractData = await b2bService.getPartyContract(pId);
        if (contractData?.item1?.contractID) {
          setContractId(contractData.item1.contractID);
          if (contractData.item1.panelID) {
            setPanelId(Number(contractData.item1.panelID));
          } else {
            setPanelId(defPanelId);
          }
        } else {
          setContractId('');
          setPanelId(defPanelId);
        }

        const elig = await b2bService.validateEligibility(pId);
        setPartyCreditStatus(elig);
      } catch (err) {
        console.error('Error resolving party contract/credit:', err);
        setPanelId(defPanelId);
      }
    } else if (selectedValue.startsWith('PANEL_')) {
      const panId = Number(selectedValue.replace('PANEL_', '')) || 1;
      setPartyId('');
      setContractId('');
      setClientOrderNumber('');
      setPartyCreditStatus(null);
      setPanelId(panId);
    }
  };

  // Master-Driven Doctor Selection Handler
  const handleDoctorChange = (id) => {
    const numId = Number(id) || 1;
    setDoctorId(numId);
    const doc = lookups.doctors.find((d) => Number(d.doctorId) === numId);
    const docN = doc ? doc.doctorName : (numId === 1 ? 'SELF / DIRECT REFERRAL' : '');
    setDoctorName(docN);

    // Commission Routing: SELF = no commission; Dr > 1 = Doctor receives commission
    if (!referralSourceId) {
      if (numId === 1) {
        setBeneficiaryType('Doctor');
        setBeneficiaryId('');
        setBeneficiaryName('');
      } else {
        setBeneficiaryType('Doctor');
        setBeneficiaryId(numId);
        setBeneficiaryName(docN);
      }
    }
  };

  // Master-Driven Referral Source (Clinic / Center / External Partner) Handler
  const handleReferralSourceChange = (selId) => {
    setReferralSourceId(selId);
    if (!selId) {
      setReferralSourceName('');
      if (Number(doctorId) > 1) {
        setBeneficiaryType('Doctor');
        setBeneficiaryId(doctorId);
        setBeneficiaryName(doctorName);
      } else {
        setBeneficiaryType('Doctor');
        setBeneficiaryId('');
        setBeneficiaryName('');
      }
      return;
    }

    const src = referralSources.find((s) => String(s.sourceID || s.sourceId || s.id) === String(selId));
    if (src) {
      const sName = src.sourceName || src.name || '';
      const sIdVal = src.sourceID || src.sourceId || src.id;
      setReferralSourceName(sName);
      setBeneficiaryType(src.sourceType || 'HOSPITAL');
      setBeneficiaryId(sIdVal);
      setBeneficiaryName(sName);

      // Auto-bind matching partner panel if exists in panel master
      const sNameLower = sName.toLowerCase();
      const matchingPanel = lookups.panels.find(
        (p) =>
          p.panelName.toLowerCase().includes(sNameLower) ||
          (src.sourceCode && p.panelCode && p.panelCode.toLowerCase() === src.sourceCode.toLowerCase())
      );
      if (matchingPanel) {
        setPanelId(matchingPanel.panelId);
      }
    } else {
      setReferralSourceName('');
      setBeneficiaryType('Doctor');
      setBeneficiaryId(doctorId > 1 ? doctorId : '');
      setBeneficiaryName(doctorId > 1 ? doctorName : '');
    }
  };

  // Order Priority: 'Routine' | 'Urgent'
  const [priority, setPriority] = useState('Routine');

  // Patient Identification Biometric & Search Status: 'idle' | 'matched' | 'unmatched' | 'error' | 'cancelled'
  const [faceIdStatus, setFaceIdStatus] = useState('idle');
  const [faceIdStatusDetail, setFaceIdStatusDetail] = useState('');

  // Automated Post-Registration Workflow Checkboxes
  const [autoCollectSample, setAutoCollectSample] = useState(false);
  const [autoReceiveSample, setAutoReceiveSample] = useState(false);

  // Collapsible section toggles
  const [guardianExpanded, setGuardianExpanded] = useState(false);
  const [identificationExpanded, setIdentificationExpanded] = useState(false);
  const [clinicalNotesExpanded, setClinicalNotesExpanded] = useState(false);
  const [documentsExpanded, setDocumentsExpanded] = useState(false);

  // Load all lookups from backend API on mount
  useEffect(() => {
    const fetchLookups = async () => {
      setLookupsLoading(true);
      try {
        const res = await lookupService.getRegistrationLookups();
        if (res.success && res.data) {
          setLookups({
            titles: res.data.titles || [],
            genders: res.data.genders || [],
            bloodGroups: res.data.bloodGroups || [],
            nationalities: res.data.nationalities || [],
            maritalStatuses: res.data.maritalStatuses || [],
            patientCategories: res.data.patientCategories || [],
            visitTypes: res.data.visitTypes || [],
            patientTypes: res.data.patientTypes || [],
            identifierTypes: res.data.identifierTypes || [],
            documentTypes: res.data.documentTypes || [],
            paymentModes: res.data.paymentModes || [],
            centres: res.data.centres || [],
            doctors: res.data.doctors || [],
            panels: res.data.panels || [],
          });

          if (res.data.centres?.length > 0 && !user?.centreId) {
            setCentreId(res.data.centres[0].centreId);
          }
        }
      } catch (err) {
        console.error('Failed to load registration lookups:', err);
        showToast('Unable to load master lookups from server.', 'error');
      } finally {
        setLookupsLoading(false);
      }
    };
    fetchLookups();
  }, [user]);

  // Active collection boys of this centre, for the "Collection Boy" field next to the address
  const [collectionBoys, setCollectionBoys] = useState([]);
  const [collectionBoysFailed, setCollectionBoysFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    collectionBoyService.lookup()
      .then((res) => { if (!cancelled) { setCollectionBoys(res.success ? (res.data || []) : []); setCollectionBoysFailed(!res.success); } })
      .catch(() => { if (!cancelled) { setCollectionBoys([]); setCollectionBoysFailed(true); } });
    return () => { cancelled = true; };
  }, [user?.centreId]);

  // Load active referral sources
  useEffect(() => {
    referralService.getSources({ activeOnly: true })
      .then(res => setReferralSources(res.data || []))
      .catch(err => console.error('Failed to load referral sources:', err));
  }, []);

  // Pre-warm neural network face models in background memory on page mount
  useEffect(() => {
    faceApiService.loadModels().catch((err) => {
      console.debug('[RegistrationPage] Background biometric model warmup:', err?.message || err);
    });
  }, []);

  // =========================================================================
  // 4. CONFIGURATION-DRIVEN SYSTEM (HIERARCHICAL RESOLUTION)
  // =========================================================================
  const [effectiveConfig, setEffectiveConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  const loadEffectiveConfig = useCallback(async (cId) => {
    setConfigLoading(true);
    try {
      const res = await registrationConfigService.getEffectiveConfig(cId);
      if (res.success && res.data) {
        setEffectiveConfig(res.data);
      }
    } catch (err) {
      console.error('Failed to load effective registration configuration:', err);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    if (centreId) {
      loadEffectiveConfig(centreId);
    }
  }, [centreId, loadEffectiveConfig]);

  useEffect(() => {
    if (effectiveConfig) {
      const canCollect = !!effectiveConfig.autoCollectOnRegistration;
      const canReceive = canCollect && !!effectiveConfig.autoReceiveOnRegistration;
      setAutoCollectSample(canCollect);
      setAutoReceiveSample(canReceive);
    }
  }, [effectiveConfig]);

  const fieldMap = useMemo(() => {
    const map = new Map();
    if (effectiveConfig?.fields) {
      for (const f of effectiveConfig.fields) {
        map.set(f.fieldKey, f);
      }
    }
    return map;
  }, [effectiveConfig]);

  const isFieldVisible = useCallback(
    (fieldKey, defaultVal = true) => {
      if (!fieldMap.has(fieldKey)) return defaultVal;
      const f = fieldMap.get(fieldKey);
      return f.effectiveVisible && f.effectiveEnabled;
    },
    [fieldMap]
  );

  const isFieldRequired = useCallback(
    (fieldKey, defaultVal = false) => {
      if (!fieldMap.has(fieldKey)) return defaultVal;
      const f = fieldMap.get(fieldKey);
      return f.effectiveVisible && f.effectiveRequired;
    },
    [fieldMap]
  );

  const isFieldReadOnly = useCallback(
    (fieldKey) => {
      return fieldMap.get(fieldKey)?.effectiveReadOnly || false;
    },
    [fieldMap]
  );

  const getFieldLabel = useCallback(
    (fieldKey, fallback) => {
      return fieldMap.get(fieldKey)?.effectiveLabel || fallback;
    },
    [fieldMap]
  );

  const getFieldPlaceholder = useCallback(
    (fieldKey, fallback = '') => {
      return fieldMap.get(fieldKey)?.placeholder || fallback;
    },
    [fieldMap]
  );

  // =========================================================================
  // 5. PATIENT IDENTIFICATION & SEARCH
  // =========================================================================
  const [selectedPatientUhid, setSelectedPatientUhid] = useState(null);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [isSearchingPatient, setIsSearchingPatient] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(false);

  // Patient Photo State
  const [patientPhotoUrl, setPatientPhotoUrl] = useState(null);
  const [patientPhotoFile, setPatientPhotoFile] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  const [isFaceScanModalOpen, setIsFaceScanModalOpen] = useState(false);
  const [enrolledFaceVector, setEnrolledFaceVector] = useState(null);
  const photoFileInputRef = useRef(null);
  const patientSearchInputRef = useRef(null);

  // Patient Documents State
  const [patientDocuments, setPatientDocuments] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('Prescription / Doctor Slip');
  const [docUploading, setDocUploading] = useState(false);
  const docFileInputRef = useRef(null);

  // Document Preview Modal State
  const [previewDoc, setPreviewDoc] = useState(null);

  // Live time for identity header
  const [, setCurrentTime] = useState(new Date().toLocaleTimeString());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date().toLocaleTimeString()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Debounced Patient Search
  useEffect(() => {
    if (!patientSearchQuery.trim() || patientSearchQuery.trim().length < 2) {
      setPatientSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearchingPatient(true);
      try {
        const res = await registrationService.searchPatients(patientSearchQuery.trim(), 10);
        if (res.success) {
          setPatientSearchResults(res.data || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsSearchingPatient(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [patientSearchQuery]);

  // Form State: Patient Demographics
  const [patientData, setPatientData] = useState({
    patientId: '',
    title: 'Mr.',
    pName: '',
    gender: 'Male',
    dob: '',
    isDobActual: false,
    ageYear: '',
    ageMonth: '',
    ageDays: '',
    mobile: '',
    alternateMobile: '',
    email: '',
    address: '',
    addressLine2: '',
    locality: '',
    city: 'Kolkata',
    cityId: 23,
    district: 'Kolkata',
    state: 'West Bengal',
    stateId: 4,
    country: 'India',
    countryId: 1,
    pincode: '',
    clinicalHistory: '',
    physicianNotes: '',
    bloodGroup: '',
    nationality: 'Indian',
    maritalStatus: 'Single',
    patientCategory: 'General',
    weight: '',
    weightUnit: 'kg',
    height: '',
    heightUnit: 'cm',
    lmp: '',
    guardianName: '',
    guardianRelation: 'Father',
    guardianMobile: '',
    guardianAddress: '',
    governmentIdType: 'Aadhaar Card',
    governmentIdNumber: '',
    abhaId: '',
  });

  // DOB vs Age Mode Toggle: 'dob' | 'age'
  const [dobMode, setDobMode] = useState('age');

  // Age Validation Error memoized
  const ageValidationError = useMemo(() => {
    return getAgeValidationError(patientData.ageYear);
  }, [patientData.ageYear]);

  const calculateAgeFromDob = (dobString) => {
    if (!dobString) return { years: '', months: '', days: '' };
    const birthDate = new Date(dobString);
    const today = new Date();
    if (isNaN(birthDate.getTime()) || birthDate > today) {
      return { years: '', months: '', days: '' };
    }
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
      months--;
      const prevMonthLastDay = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
      days += prevMonthLastDay;
    }
    if (months < 0) {
      years--;
      months += 12;
    }
    return {
      years: Math.max(0, years),
      months: Math.max(0, months),
      days: Math.max(0, days),
    };
  };

  const deriveDobFromAge = (years, months, days) => {
    if ((years === '' || years === null || years === undefined) &&
        (months === '' || months === null || months === undefined) &&
        (days === '' || days === null || days === undefined)) {
      return '';
    }
    const y = parseInt(years, 10);
    const m = parseInt(months, 10);
    const d = parseInt(days, 10);
    if (isNaN(y) && isNaN(m) && isNaN(d)) return '';
    const numY = isNaN(y) ? 0 : y;
    const numM = isNaN(m) ? 0 : m;
    const numD = isNaN(d) ? 0 : d;
    if (numY === 0 && numM === 0 && numD === 0 && years === '' && months === '' && days === '') return '';
    const now = new Date();
    const derived = new Date(now.getFullYear() - numY, now.getMonth() - numM, now.getDate() - numD);
    if (isNaN(derived.getTime())) return '';
    const yearStr = derived.getFullYear();
    const monthStr = String(derived.getMonth() + 1).padStart(2, '0');
    const dayStr = String(derived.getDate()).padStart(2, '0');
    return `${yearStr}-${monthStr}-${dayStr}`;
  };

  const handleDobChange = (dobString) => {
    setDobMode('dob');
    if (!dobString) {
      setPatientData((prev) => ({
        ...prev,
        dob: '',
        isDobActual: false,
        ageYear: '',
        ageMonth: '',
        ageDays: '',
      }));
      return;
    }

    const birthDate = new Date(dobString);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (birthDate > today) {
      showToast('Date of Birth cannot be in the future.', 'error');
      return;
    }

    const { years, months, days } = calculateAgeFromDob(dobString);
    if (typeof years === 'number' && years > 120) {
      showToast('Are you sure you entered the correct age? Maximum allowed age is 120 years.', 'error');
    }
    setPatientData((prev) => ({
      ...prev,
      dob: dobString,
      isDobActual: true,
      ageYear: years,
      ageMonth: months,
      ageDays: days,
    }));
  };

  const handleAgeChange = (years, months = '', days = '') => {
    setDobMode('age');
    let yVal = years;
    if (typeof yVal === 'string') {
      yVal = yVal.trim();
    }
    let mVal = months;
    if (typeof mVal === 'string') {
      mVal = mVal.trim();
    }
    let dVal = days;
    if (typeof dVal === 'string') {
      dVal = dVal.trim();
    }

    const numY = parseInt(yVal, 10);
    const numM = parseInt(mVal, 10);
    const numD = parseInt(dVal, 10);

    let derivedDob = '';
    if (yVal !== '' && !isNaN(numY) && numY >= 0 && numY <= 120) {
      derivedDob = deriveDobFromAge(numY, isNaN(numM) ? 0 : numM, isNaN(numD) ? 0 : numD);
    }

    setPatientData((prev) => ({
      ...prev,
      ageYear: yVal,
      ageMonth: mVal,
      ageDays: dVal,
      dob: derivedDob,
      isDobActual: false,
    }));
  };

  // Load patient documents
  const loadPatientDocuments = useCallback(async (uhid) => {
    if (!uhid) return;
    try {
      const res = await patientDocumentService.getPatientDocuments(uhid);
      if (res.success && res.data) {
        setPatientDocuments(res.data);
      }
    } catch (err) {
      console.error('Failed to load patient documents:', err);
    }
  }, []);

  const handleSelectExistingPatient = (patient) => {
    setSelectedPatientUhid(patient.patientId);
    if (patient.dob) {
      setDobMode('dob');
    } else {
      setDobMode('age');
    }

    setPatientData({
      patientId: patient.patientId,
      title: patient.title || 'Mr.',
      pName: patient.pName || '',
      gender: patient.gender || 'Male',
      dob: patient.dob ? patient.dob.split('T')[0] : '',
      isDobActual: !!patient.dob,
      ageYear: (patient.ageYear !== null && patient.ageYear !== undefined) ? patient.ageYear : '',
      ageMonth: (patient.ageMonth !== null && patient.ageMonth !== undefined) ? patient.ageMonth : '',
      ageDays: (patient.ageDays !== null && patient.ageDays !== undefined) ? patient.ageDays : '',
      mobile: patient.mobile || '',
      alternateMobile: '',
      email: patient.email || '',
      address: patient.address || '',
      addressLine2: '',
      locality: '',
      city: patient.city || 'Kolkata',
      district: 'Kolkata',
      state: patient.state || 'West Bengal',
      country: 'India',
      pincode: patient.pincode || '',
      clinicalHistory: patient.clinicalHistory || '',
      physicianNotes: '',
      bloodGroup: patient.bloodGroup || '',
      nationality: 'Indian',
      maritalStatus: 'Single',
      patientCategory: 'General',
      weight: patient.weight || '',
      weightUnit: patient.weightUnit || 'kg',
      height: patient.height || '',
      heightUnit: patient.heightUnit || 'cm',
      lmp: patient.lmp ? patient.lmp.split('T')[0] : '',
      guardianName: patient.guardianName || '',
      guardianRelation: patient.guardianRelation || 'Father',
      guardianMobile: patient.guardianMobile || '',
      guardianAddress: '',
      governmentIdType: patient.governmentIdType || 'Aadhaar Card',
      governmentIdNumber: patient.governmentIdNumber || '',
      abhaId: patient.abhaId || '',
    });

    if (patient.photoUrl) {
      const baseURL = FILE_BASE_URL;
      setPatientPhotoUrl(patient.photoUrl.startsWith('http') ? patient.photoUrl : `${baseURL}${patient.photoUrl}`);
    } else {
      setPatientPhotoUrl(null);
    }

    loadPatientDocuments(patient.patientId);
    setFaceIdStatus('matched');
    setFaceIdStatusDetail(`Identity Matched: ${patient.fullName || patient.pName} (${patient.patientId})`);
    setPatientSearchQuery('');
    setPatientSearchResults([]);
    setDuplicateWarning(false);
    showToast(`Patient ${patient.fullName || patient.pName} (${patient.patientId}) loaded.`);
  };

  // ===========================================================================
  // APPOINTMENT HAND-OFF  (/registration?appointmentId=N)
  // Loads the CONFIRMED appointment from the server (never trusts the URL for data), pre-fills the patient section
  // through the same path a manual search uses, and sends appointmentId with the normal registration payload so the
  // server marks the appointment REGISTERED inside the registration transaction. Walk-ins never reach this code.
  // ===========================================================================
  const location = useLocation();
  const navigate = useNavigate();
  const [linkedAppointment, setLinkedAppointment] = useState(null);
  const [appointmentLinkError, setAppointmentLinkError] = useState('');
  const loadedAppointmentRef = useRef(null);

  useEffect(() => {
    const appointmentId = parseAppointmentIdParam(location.search);
    if (!appointmentId || loadedAppointmentRef.current === appointmentId) return;
    loadedAppointmentRef.current = appointmentId;

    (async () => {
      try {
        const res = await appointmentService.get(appointmentId);
        const a = res.success ? res.data : null;
        if (!a) {
          setAppointmentLinkError('The appointment could not be loaded.');
          return;
        }
        if (!canRegisterAppointment(a)) {
          setAppointmentLinkError(
            a.status === 'REGISTERED'
              ? `Appointment ${a.appointmentNo} has already been registered (LabNo ${a.registeredLabNo || '-'}).`
              : `Appointment ${a.appointmentNo} is ${a.status}. Only a CONFIRMED appointment can be registered.`
          );
          return;
        }

        const prefill = buildRegistrationPrefill(a);
        setAppointmentLinkError('');
        setLinkedAppointment({ appointmentId: a.appointmentId, appointmentNo: a.appointmentNo, tokenNo: a.tokenNo || null });
        if (prefill.centreId) setCentreId(prefill.centreId);
        setVisitType(prefill.visitType);
        if (prefill.doctorId) {
          setDoctorId(prefill.doctorId);
          setDoctorName(prefill.doctorName);
        }

        if (prefill.patientId) {
          const pRes = await registrationService.getPatient(prefill.patientId);
          if (pRes.success && pRes.data) handleSelectExistingPatient(pRes.data);
          else setAppointmentLinkError(`Patient ${prefill.patientId} of this appointment could not be loaded.`);
        } else {
          setDobMode(prefill.patientData.dob ? 'dob' : 'age');
          setPatientData((prev) => ({ ...prev, ...prefill.patientData }));
        }
        // The tests chosen at booking go into the order cart through the normal path, which prices them for the current panel.
        const bookedTests = Array.isArray(a.tests) ? a.tests : [];
        // Each test is looked up again in the live catalogue (never taken from the appointment), so a deactivated or changed
        // test is handled exactly like one picked by hand, and a stored name or price can never reach the bill.
        let addedTests = 0;
        for (const t of bookedTests) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const found = await masterService.getInvestigations({ search: t.code || t.name, isActive: true, pageSize: 25 });
            const inv = found.success && found.data?.items ? found.data.items.find((x) => x.itemId === t.itemId) : null;
            if (inv) {
              // eslint-disable-next-line no-await-in-loop
              await handleAddTest(catalogItemFromInvestigation(inv));
              addedTests += 1;
            } else {
              showToast(`"${t.name}" is no longer available and was not added.`, 'error');
            }
          } catch (err) {
            showToast(`"${t.name}" could not be added. Add it by hand.`, 'error');
          }
        }
        showToast(
          bookedTests.length > 0
            ? `Appointment ${a.appointmentNo} loaded with ${addedTests} of ${bookedTests.length} test(s). Check the order and complete the registration.`
            : `Appointment ${a.appointmentNo} loaded. Add the tests and complete the registration.`
        );
      } catch (err) {
        setAppointmentLinkError(err.response?.data?.message || 'The appointment could not be loaded.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const clearAppointmentLink = () => {
    setLinkedAppointment(null);
    setAppointmentLinkError('');
    loadedAppointmentRef.current = null;
    navigate('/registration', { replace: true });
  };

  // A patient who walks in without using the appointment link: if today's list holds an open appointment for this UHID or
  // mobile, offer it, so the same visit is not registered twice (once from the appointment and once as a walk-in).
  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [dismissedAppointmentIds, setDismissedAppointmentIds] = useState([]);
  useEffect(() => {
    if (linkedAppointment) {
      setTodaysAppointments([]);
      return undefined;
    }
    const digits = (patientData.mobile || '').replace(/\D/g, '');
    if (!selectedPatientUhid && digits.length !== 10) {
      setTodaysAppointments([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await appointmentService.forPatient({
          patientId: selectedPatientUhid || undefined,
          mobile: digits.length === 10 ? digits : undefined,
        });
        if (!cancelled && res.success) setTodaysAppointments(res.data || []);
      } catch (err) {
        // No access to appointments, or the module is not installed: there is simply nothing to suggest.
        if (!cancelled) setTodaysAppointments([]);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [selectedPatientUhid, patientData.mobile, linkedAppointment]);

  const registerFromTodaysAppointment = async (appt) => {
    try {
      // The patient is here: mark the arrival (confirms a booked appointment and gives the token) before opening it.
      if (!appt.arrivedAt) {
        const res = await appointmentService.arrive(appt.appointmentId, { expectedVersion: appt.version });
        if (!res.success) {
          showToast(res.message || 'Could not open the appointment.', 'error');
          return;
        }
      }
      setTodaysAppointments([]);
      navigate(`/registration?appointmentId=${appt.appointmentId}`);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not open the appointment.', 'error');
    }
  };

  const isSubmittingRef = useRef(false);
  const generateIdempotencyKey = () => (
    window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `reg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  );
  const idempotencyKeyRef = useRef(generateIdempotencyKey());

  const handleResetToNewPatient = () => {
    setSelectedPatientUhid(null);
    setFaceIdStatus('idle');
    setFaceIdStatusDetail('');
    setPatientSearchQuery('');
    setPatientSearchResults([]);
    setPatientPhotoUrl(null);
    setPatientPhotoFile(null);
    setEnrolledFaceVector(null);
    setPatientDocuments([]);
    setDobMode('age');
    setPriority('Routine');
    setPatientData({
      patientId: '',
      title: 'Mr.',
      pName: '',
      gender: 'Male',
      dob: '',
      isDobActual: false,
      ageYear: '',
      ageMonth: '',
      ageDays: '',
      mobile: '',
      alternateMobile: '',
      email: '',
      address: '',
      addressLine2: '',
      locality: '',
      city: 'Kolkata',
      district: 'Kolkata',
      state: 'West Bengal',
      country: 'India',
      pincode: '',
      clinicalHistory: '',
      physicianNotes: '',
      bloodGroup: '',
      nationality: 'Indian',
      maritalStatus: 'Single',
      patientCategory: 'General',
      weight: '',
      weightUnit: 'kg',
      height: '',
      heightUnit: 'cm',
      lmp: '',
      guardianName: '',
      guardianRelation: 'Father',
      guardianMobile: '',
      guardianAddress: '',
      governmentIdType: 'Aadhaar Card',
      governmentIdNumber: '',
      abhaId: '',
    });
    setDuplicateWarning(false);
    setSelectedTests([]);
    setDiscountOnTotal(0);
    setDiscountReason('');
    setSelectedReasonId('');
    setDiscountRemarks('');
    setDiscountValidation(null);
    setApprovalRequest({ status: 'IDLE', requestId: null, requestNo: '', approvedByName: '', rejectionReason: '', submitting: false });
    setCareCoinsWallet(null);
    setCareCoinsPointsToRedeem('');
    setCollectionCharge({ amount: 0, entry: null, error: false, note: null });
    setCareCoinsPreview(null);
    setCareCoinsPreviewError(null);
    setPaymentsList([]);
    setCurrentPayAmount('');
    setIsPayAmountManuallyEdited(false);
    setCashReceived('');
    setReferralSourceId('');
    setReferralSourceName('');
    setDoctorId(1);
    setDoctorName('SELF / DIRECT REFERRAL');
    setBeneficiaryType('Doctor');
    setBeneficiaryId('');
    setBeneficiaryName('');
    setPartyId('');
    setContractId('');
    setClientOrderNumber('');
    setPartyCreditStatus(null);
    setPanelId(1);
    setPhlebotomistName('');
    setShowAdditionalDetails(false);
    setCatalogSearch('');
    setInvestigationsCatalog([]);
    setHighlightedIndex(-1);
    idempotencyKeyRef.current = generateIdempotencyKey();
    setActiveTab('patient_clinical');
  };

  const checkDuplicate = useCallback(async () => {
    if (selectedPatientUhid || !patientData.mobile || !patientData.pName) return;
    if (patientData.mobile.length === 10 && patientData.pName.trim().length >= 3) {
      try {
        const res = await registrationService.checkDuplicate(
          patientData.mobile,
          patientData.pName.trim(),
          selectedPatientUhid
        );
        setDuplicateWarning(!!res.data?.isDuplicate);
      } catch (err) {
        console.error(err);
      }
    }
  }, [patientData.mobile, patientData.pName, selectedPatientUhid]);

  // Photo Upload Handler
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      return showToast('Image file cannot exceed 5MB.', 'error');
    }

    const localPreviewUrl = URL.createObjectURL(file);
    setPatientPhotoUrl(localPreviewUrl);
    setPatientPhotoFile(file);

    // If existing patient, upload directly to backend
    if (selectedPatientUhid) {
      setPhotoUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('patientId', selectedPatientUhid);
        const res = await patientDocumentService.uploadPhoto(formData);
        if (res.success && res.data?.photoUrl) {
          const baseURL = FILE_BASE_URL;
          setPatientPhotoUrl(`${baseURL}${res.data.photoUrl}`);
          showToast('Patient photo updated successfully.');
        }
      } catch (err) {
        console.error('Failed to upload photo:', err);
        showToast('Failed to persist photo to server.', 'error');
      } finally {
        setPhotoUploading(false);
      }
    }
  };

  // Live Camera Photo Capture Handler
  const handlePhotoFromCamera = async (file, previewUrl) => {
    setPatientPhotoUrl(previewUrl);
    setPatientPhotoFile(file);

    // Extract facial biometric descriptor in background for automatic enrollment
    try {
      const img = new Image();
      img.src = previewUrl;
      img.onload = async () => {
        try {
          const faceRes = await faceApiService.detectFaceAndDescriptor(img);
          if (faceRes && faceRes.descriptor) {
            setEnrolledFaceVector(faceRes.descriptor);
            if (selectedPatientUhid) {
              await faceAuthService.enrollPatientFace(selectedPatientUhid, faceRes.descriptor);
              console.info('[Registration] Automatically enrolled patient face biometric for', selectedPatientUhid);
            }
          }
        } catch (fErr) {
          console.warn('[Registration] Background face feature extraction skipped:', fErr);
        }
      };
    } catch {
      // non-blocking
    }

    // If existing patient, upload directly to backend
    if (selectedPatientUhid) {
      setPhotoUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('patientId', selectedPatientUhid);
        const res = await patientDocumentService.uploadPhoto(formData);
        if (res.success && res.data?.photoUrl) {
          const baseURL = FILE_BASE_URL;
          setPatientPhotoUrl(`${baseURL}${res.data.photoUrl}`);
          showToast('Patient photo captured & updated successfully.');
        }
      } catch (err) {
        console.error('Failed to persist camera photo:', err);
        showToast('Failed to persist photo to server.', 'error');
      } finally {
        setPhotoUploading(false);
      }
    } else {
      showToast('Patient photo captured successfully.');
    }
  };

  // Face Scan Identification Handler
  const handlePatientFaceScanSuccess = async ({ faceEncoding }) => {
    try {
      const result = await faceAuthService.identifyPatientByFace(faceEncoding);
      setIsFaceScanModalOpen(false);

      if (result.hasMatch) {
        if (result.matchedPatient) {
          handleSelectExistingPatient(result.matchedPatient);
          const conf = Math.round((result.bestConfidence || 0) * 100);
          setFaceIdStatus('matched');
          setFaceIdStatusDetail(`Registered Patient — Identity Matched (${conf}%): ${result.matchedPatient.fullName || result.matchedPatient.pName} (${result.matchedPatient.patientId})`);
          showToast(`Patient matched with high confidence (${conf}%): ${result.matchedPatient.fullName || result.matchedPatient.pName}`);
        } else if (result.candidates && result.candidates.length > 0) {
          const patients = result.candidates.map((c) => c.patient);
          setPatientSearchResults(patients);
          setFaceIdStatus('matched');
          setFaceIdStatusDetail(`${result.candidates.length} candidate match(es) found. Please select patient from list.`);
          showToast(`Found ${result.candidates.length} candidate match(es). Please select patient.`);
        }
      } else {
        setFaceIdStatus('unmatched');
        setFaceIdStatusDetail(result.message || 'Face not found in registered patients. You can continue with manual verification or register the patient now.');
        showToast('Patient Not Registered — Manual Check Available', 'info');
      }
    } catch (err) {
      console.error('Patient face identification error:', err);
      setFaceIdStatus('error');
      setFaceIdStatusDetail(err.response?.data?.message || err.message || 'Face could not be verified — Try Again or Search Manually');
      showToast(err.response?.data?.message || err.message || 'Face identification failed.', 'error');
    }
  };

  const handleRemovePhoto = () => {
    setPatientPhotoUrl(null);
    setPatientPhotoFile(null);
    if (photoFileInputRef.current) photoFileInputRef.current.value = '';
  };

  // Document Upload Handler
  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      return showToast('Document size cannot exceed 10MB.', 'error');
    }

    setDocUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('patientId', selectedPatientUhid || 'TEMP_PATIENT');
      formData.append('documentType', selectedDocType);
      formData.append('documentName', file.name);
      formData.append('documentStatus', 'Received');

      const res = await patientDocumentService.uploadDocument(formData);
      if (res.success && res.data) {
        setPatientDocuments((prev) => [res.data, ...prev]);
        showToast(`Document "${file.name}" uploaded successfully.`);
      }
    } catch (err) {
      console.error('Failed to upload document:', err);
      showToast('Document upload failed.', 'error');
    } finally {
      setDocUploading(false);
      if (docFileInputRef.current) docFileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      const res = await patientDocumentService.deleteDocument(documentId);
      if (res.success) {
        setPatientDocuments((prev) => prev.filter((d) => d.documentId !== documentId));
        showToast('Document removed.');
      }
    } catch (err) {
      showToast('Failed to delete document.', 'error');
    }
  };

  // =========================================================================
  // 6. INVESTIGATION & SERVICE CART & ORDER ENTRY WORKBENCH
  // =========================================================================
  const [investigationsCatalog, setInvestigationsCatalog] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);
  const [selectedTests, setSelectedTests] = useState([]);
  const [categoryFilter] = useState('All'); // 'All' | 'Tests' | 'Profiles' | 'Packages' | 'Pathology' | 'Radiology' | 'Urgent'

  // Live unified catalog search across Tests, Profiles, and Packages
  useEffect(() => {
    if (!catalogSearch.trim() || catalogSearch.trim().length < 2) {
      setInvestigationsCatalog([]);
      return;
    }
    const timer = setTimeout(async () => {
      setCatalogLoading(true);
      try {
        const query = catalogSearch.trim();
        const promises = [];

        // 1. Tests / Investigations
        if (['All', 'Tests', 'Pathology', 'Radiology', 'Urgent'].includes(categoryFilter)) {
          promises.push(
            masterService
              .getInvestigations({
                search: query,
                isActive: true,
                pageSize: 25,
              })
              .then((res) =>
                res.success && res.data?.items
                  ? res.data.items.map((inv) => ({
                      itemType: 'TEST',
                      investigationId: inv.investigationId,
                      itemId: inv.itemId,
                      code: inv.testCode,
                      name: inv.name,
                      category: inv.departmentName || 'Diagnostics',
                      sample: inv.primarySampleName || 'Blood / Serum',
                      componentsCount: 1,
                      baseRate: parseFloat(inv.baseRate) || 0,
                      reportType: inv.reportType || 0,
                      subCategoryId: inv.subCategoryId,
                      sampleTypeId: inv.defaultSampleTypeId,
                      containerId: inv.defaultContainerId,
                      processingMode: inv.processingMode || 'IN_HOUSE',
                      isOutsource: inv.processingMode === 'OUTSOURCE' || Boolean(inv.defaultReferenceLabId) || Boolean(inv.referenceLabName),
                      referenceLabName: inv.referenceLabName || null,
                      defaultReferenceLabId: inv.defaultReferenceLabId || null,
                    }))
                  : []
              )
              .catch(() => [])
          );
        }

        // 2. Clinical Profiles
        if (['All', 'Profiles'].includes(categoryFilter)) {
          promises.push(
            profileService
              .getProfiles({
                search: query,
                isActive: true,
              })
              .then((res) =>
                res.success && res.data
                  ? res.data.map((p) => ({
                      itemType: 'PROFILE',
                      investigationId: 0,
                      itemId: p.itemId,
                      code: p.profileCode,
                      name: p.profileName,
                      category: p.departmentName || 'Clinical Profile',
                      sample: `Profile Bundle (${p.activeComponentCount || 0} tests)`,
                      componentsCount: p.activeComponentCount || 0,
                      baseRate: 0,
                      reportType: 0,
                      subCategoryId: p.subCategoryId,
                      sampleTypeId: 0,
                      containerId: 0,
                    }))
                  : []
              )
              .catch(() => [])
          );
        }

        // 3. Health Packages
        if (['All', 'Packages'].includes(categoryFilter)) {
          promises.push(
            packageService
              .getPackages({
                search: query,
                isActive: true,
              })
              .then((res) =>
                res.success && res.data
                  ? res.data.map((pkg) => ({
                      itemType: 'PACKAGE',
                      investigationId: 0,
                      itemId: pkg.itemId,
                      code: pkg.packageCode,
                      name: pkg.packageName,
                      category: 'Health Package',
                      sample: `Multi-Test Package (${pkg.activeComponentCount || 0} tests)`,
                      componentsCount: pkg.activeComponentCount || 0,
                      baseRate: 0,
                      reportType: 0,
                      subCategoryId: 0,
                      sampleTypeId: 0,
                      containerId: 0,
                    }))
                  : []
              )
              .catch(() => [])
          );
        }

        const results = await Promise.all(promises);
        setInvestigationsCatalog(results.flat());
      } catch (err) {
        console.error('Unified catalog search error:', err);
      } finally {
        setCatalogLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [catalogSearch, categoryFilter]);

  // Reset or initialize highlighted index when catalog search results change
  useEffect(() => {
    if (investigationsCatalog.length > 0) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [investigationsCatalog]);

  // Auto-scroll highlighted catalog item into view smoothly
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownListRef.current) {
      const activeEl = dropdownListRef.current.querySelector(`.test-result-item[data-index="${highlightedIndex}"]`);
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  // Keyboard navigation handler for test search: ArrowDown, ArrowUp, Enter, Escape
  const handleSearchKeyDown = (e) => {
    if (investigationsCatalog.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < investigationsCatalog.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : investigationsCatalog.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < investigationsCatalog.length) {
        handleAddTest(investigationsCatalog[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setInvestigationsCatalog([]);
      setHighlightedIndex(-1);
    }
  };

  // Add Item to Order Cart with Panel Tariff Resolution
  const handleAddTest = async (inv) => {
    if (selectedTests.some((t) => t.itemId === inv.itemId)) {
      showToast(`"${inv.name}" is already in the order cart.`, 'error');
      return;
    }

    // Resolve rate for the currently selected panel/party
    let resolvedRate = inv.baseRate || 0;
    let resolvedMrp = inv.baseRate || 0;
    try {
      if (inv.itemId) {
        const rateRes = await partyService.resolveRate({
          panelId: Number(panelId) || 1,
          partyId: partyId ? Number(partyId) : undefined,
          itemType: inv.itemType || 'TEST',
          itemId: inv.itemId,
          centreId: Number(centreId) || 1,
        });
        if (rateRes.success && rateRes.data && rateRes.data.resolvedRate > 0) {
          resolvedRate = rateRes.data.resolvedRate;
          resolvedMrp = rateRes.data.mrpRate || resolvedRate;
        }
      }
    } catch (e) {
      console.warn('Rate resolution fallback:', e);
    }

    const newTest = {
      investigationId: inv.investigationId || 0,
      itemId: inv.itemId,
      itemCode: inv.code,
      itemName: inv.name,
      itemType: inv.itemType || 'TEST',
      subCategoryId: inv.subCategoryId || 0,
      subCategoryName: inv.category || 'Diagnostics',
      rate: parseFloat(resolvedRate) || 0,
      mrpRate: parseFloat(resolvedMrp) || parseFloat(resolvedRate) || 0,
      discountAmt: 0,
      quantity: 1,
      sampleTypeId: inv.sampleTypeId || 0,
      sampleTypeName: inv.sample || 'Blood / Serum',
      containerId: inv.containerId || 0,
      reportType: inv.reportType || 0,
      isUrgent: false,
      componentsCount: inv.componentsCount || 1,
      processingMode: inv.processingMode || 'IN_HOUSE',
      isOutsource: inv.isOutsource || false,
      referenceLabName: inv.referenceLabName || null,
      defaultReferenceLabId: inv.defaultReferenceLabId || null,
    };

    setSelectedTests((prev) => [...prev, newTest]);
    setCatalogSearch('');
    setInvestigationsCatalog([]);
    setHighlightedIndex(-1);
  };

  // Re-resolve rates automatically when panel, party or centre changes for all items currently in cart
  useEffect(() => {
    if (selectedTests.length === 0) return;
    const reResolveAll = async () => {
      try {
        const updated = await Promise.all(
          selectedTests.map(async (test) => {
            if (!test.itemId) return test;
            try {
              const res = await partyService.resolveRate({
                panelId: Number(panelId) || 1,
                partyId: partyId ? Number(partyId) : undefined,
                itemType: test.itemType || 'TEST',
                itemId: test.itemId,
                centreId: Number(centreId) || 1,
              });
              if (res.success && res.data && res.data.resolvedRate > 0) {
                return {
                  ...test,
                  rate: res.data.resolvedRate,
                  mrpRate: res.data.mrpRate || res.data.resolvedRate,
                };
              }
            } catch (err) {
              // keep existing
            }
            return test;
          })
        );
        setSelectedTests(updated);
      } catch (err) {
        console.warn('Failed to re-resolve cart rates on panel change:', err);
      }
    };
    reResolveAll();
  }, [panelId, partyId, centreId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveTest = (itemId) => {
    const testToRemove = selectedTests.find((t) => t.itemId === itemId);
    if (!testToRemove) return;

    // Financial Safety Guard: Check if removing this test causes net amount to drop below totalPaid
    const remainingTests = selectedTests.filter((t) => t.itemId !== itemId);
    const newGross = Math.round(
      (remainingTests.reduce((sum, t) => sum + (Number(t.rate) || 0) * (Number(t.quantity) || 1), 0) + Number.EPSILON) * 100
    ) / 100;
    let newDisc = 0;
    const rawDisc = parseFloat(discountOnTotal) || 0;
    if (rawDisc > 0) {
      if (discountType === 'Percentage') {
        newDisc = Math.round(((newGross * Math.min(100, Math.max(0, rawDisc))) / 100 + Number.EPSILON) * 100) / 100;
      } else {
        newDisc = Math.round((Math.min(newGross, Math.max(0, rawDisc)) + Number.EPSILON) * 100) / 100;
      }
    }
    const newNet = Math.round((Math.max(0, newGross - newDisc) + (collectionCharge.amount || 0) + Number.EPSILON) * 100) / 100;

    if (totalPaid > newNet + 0.001) {
      return showToast(
        `Cannot remove "${testToRemove.itemName}": Payments already collected (₹${totalPaid.toFixed(2)}) would exceed the new Net Payable (₹${newNet.toFixed(2)}). Please remove or adjust payments from the Payment History table first.`,
        'error'
      );
    }

    setSelectedTests(remainingTests);
  };

  const handleToggleUrgent = (itemId) => {
    setSelectedTests((prev) =>
      prev.map((t) => (t.itemId === itemId ? { ...t, isUrgent: !t.isUrgent } : t))
    );
  };

  // Investigation & Service Summary Computations
  const invStats = useMemo(() => {
    const total = selectedTests.length;
    const tests = selectedTests.filter((t) => !t.itemType || t.itemType === 'TEST').length;
    const profiles = selectedTests.filter((t) => t.itemType === 'PROFILE').length;
    const packages = selectedTests.filter((t) => t.itemType === 'PACKAGE').length;
    const pathology = selectedTests.filter((t) => t.reportType === 0 && (!t.itemType || t.itemType === 'TEST')).length;
    const radiology = selectedTests.filter((t) => t.reportType === 1 && (!t.itemType || t.itemType === 'TEST')).length;
    const urgent = selectedTests.filter((t) => t.isUrgent).length;
    return { total, tests, profiles, packages, pathology, radiology, urgent };
  }, [selectedTests]);

  // Filtered investigations in cart
  const displayedTests = useMemo(() => {
    if (categoryFilter === 'Tests') return selectedTests.filter((t) => !t.itemType || t.itemType === 'TEST');
    if (categoryFilter === 'Profiles') return selectedTests.filter((t) => t.itemType === 'PROFILE');
    if (categoryFilter === 'Packages') return selectedTests.filter((t) => t.itemType === 'PACKAGE');
    if (categoryFilter === 'Pathology') return selectedTests.filter((t) => t.reportType === 0);
    if (categoryFilter === 'Radiology') return selectedTests.filter((t) => t.reportType === 1);
    if (categoryFilter === 'Urgent') return selectedTests.filter((t) => t.isUrgent);
    return selectedTests;
  }, [selectedTests, categoryFilter]);

  // =========================================================================
  // 7. BILLING WORKBENCH & FINANCIAL CALCULATIONS
  // =========================================================================
  const [discountOnTotal, setDiscountOnTotal] = useState(0);
  const [discountType, setDiscountType] = useState('Amount'); // 'Amount' or 'Percentage'
  const [discountReason, setDiscountReason] = useState('');

  // Discount Authority & Approval Workflow State
  const [discountReasons, setDiscountReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [discountRemarks, setDiscountRemarks] = useState('');
  const [discountValidation, setDiscountValidation] = useState(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [approvalRequest, setApprovalRequest] = useState({
    status: 'IDLE', // 'IDLE' | 'PENDING' | 'APPROVED' | 'REJECTED'
    requestId: null,
    requestNo: '',
    approvedByName: '',
    rejectionReason: '',
    submitting: false
  });

  // CareCoins redemption (patient loyalty/rewards — see docs/carecoins/CARECOINS_DESIGN.md).
  // Mirrors the discount section's pattern: a value the cashier proposes here is only an
  // estimate; the server re-validates and applies it atomically with the rest of the bill
  // at registration commit time (CareCoinsService.CommitRedemptionAsync, wired into
  // RegistrationRepository.CreateRegistrationTransactionAsync).
  const [careCoinsWallet, setCareCoinsWallet] = useState(null); // { balancePoints, estimatedRedemptionValueINR, ... }
  const [careCoinsWalletLoading, setCareCoinsWalletLoading] = useState(false);
  const [careCoinsPointsToRedeem, setCareCoinsPointsToRedeem] = useState('');
  // Collection charge estimate reported by <CollectionChargePanel>. The server recalculates and re-authorises it on save.
  const [collectionCharge, setCollectionCharge] = useState({ amount: 0, entry: null, error: false, note: null });
  const [careCoinsPreview, setCareCoinsPreview] = useState(null); // { isEligible, estimatedDiscountINR, allowStackingWithDiscount, ... }
  const [careCoinsPreviewError, setCareCoinsPreviewError] = useState(null);
  const [isPreviewingCareCoins, setIsPreviewingCareCoins] = useState(false);

  // Load master discount reasons on mount
  useEffect(() => {
    discountService.getReasons()
      .then((res) => {
        if (res.success && res.data) {
          setDiscountReasons(res.data);
          if (res.data.length > 0 && !discountReason) {
            setDiscountReason(res.data[0].reasonName);
            setSelectedReasonId(String(res.data[0].reasonId));
          }
        }
      })
      .catch((err) => console.error('Failed to load discount reasons:', err));
  }, []);

  // Financial Precision Rounding Helper
  const roundMoney = (val) => Math.round((Number(val) + Number.EPSILON) * 100) / 100;

  const grossAmount = useMemo(() => {
    const sum = selectedTests.reduce((acc, t) => acc + (Number(t.rate) || 0) * (Number(t.quantity) || 1), 0);
    return roundMoney(sum);
  }, [selectedTests]);

  const discountAmount = useMemo(() => {
    const raw = parseFloat(discountOnTotal);
    if (isNaN(raw) || raw <= 0) return 0;
    if (discountType === 'Percentage') {
      const pct = Math.min(100, Math.max(0, raw));
      return roundMoney((grossAmount * pct) / 100);
    }
    return roundMoney(Math.min(grossAmount, Math.max(0, raw)));
  }, [grossAmount, discountOnTotal, discountType]);

  // CareCoins — fetch the patient's wallet balance once a patient is resolved. A disabled/
  // never-used rule (IsEnabled=0, the current default per CARECOINS_DESIGN.md's fail-closed
  // default) means every wallet balance is 0, which the render below uses to hide the
  // redemption input entirely rather than showing a confusing always-zero section.
  useEffect(() => {
    if (!selectedPatientUhid) {
      setCareCoinsWallet(null);
      setCareCoinsPointsToRedeem('');
      setCareCoinsPreview(null);
      setCareCoinsPreviewError(null);
      return;
    }

    let cancelled = false;
    setCareCoinsWalletLoading(true);
    carecoinsService.getWallet(selectedPatientUhid)
      .then((res) => {
        if (cancelled) return;
        setCareCoinsWallet(res.success && res.data ? res.data : null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('CareCoins wallet fetch error:', err);
        setCareCoinsWallet(null);
      })
      .finally(() => {
        if (!cancelled) setCareCoinsWalletLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedPatientUhid]);

  // Live CareCoins redemption preview — read-only estimate only; the server re-validates
  // everything again at commit time (balance, per-invoice cap, stacking rule), same
  // discipline as the discount authority check below.
  useEffect(() => {
    const pts = parseInt(careCoinsPointsToRedeem, 10);
    if (!selectedPatientUhid || !pts || pts <= 0) {
      setCareCoinsPreview(null);
      setCareCoinsPreviewError(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsPreviewingCareCoins(true);
      setCareCoinsPreviewError(null);
      try {
        const res = await carecoinsService.previewRedemption({
          patientId: selectedPatientUhid,
          centreId: Number(centreId) || 1,
          pointsRequested: pts,
        });
        if (res.success && res.data) {
          setCareCoinsPreview(res.data);
        } else {
          setCareCoinsPreview(null);
          setCareCoinsPreviewError(res.message || 'Unable to preview CareCoins redemption.');
        }
      } catch (err) {
        setCareCoinsPreview(null);
        setCareCoinsPreviewError(err.response?.data?.message || err.message || 'Unable to preview CareCoins redemption.');
      } finally {
        setIsPreviewingCareCoins(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [careCoinsPointsToRedeem, selectedPatientUhid, centreId]);

  // Estimated CareCoins discount folded into the client-side netAmount the same way
  // discountAmount already is, so remainingBalance/totalPaid/isOverpaid auto-adjust — the
  // server is still the sole authority on the real value at commit time.
  const careCoinsDiscountEstimate = (careCoinsPreview?.isEligible && !careCoinsPreviewError)
    ? (Number(careCoinsPreview.estimatedDiscountINR) || 0)
    : 0;

  const netAmount = useMemo(() => {
    // Collection charge is billed separately from the tests and added to what the patient owes.
    return roundMoney(Math.max(0, grossAmount - discountAmount - careCoinsDiscountEstimate) + (collectionCharge.amount || 0));
  }, [grossAmount, discountAmount, careCoinsDiscountEstimate, collectionCharge.amount]);

  // Live Discount Authority Verification
  useEffect(() => {
    if (discountAmount <= 0) {
      setDiscountValidation(null);
      setApprovalRequest({ status: 'IDLE', requestId: null, requestNo: '', approvedByName: '', rejectionReason: '', submitting: false });
      return;
    }

    const timer = setTimeout(async () => {
      setIsValidatingDiscount(true);
      try {
        const pct = grossAmount > 0 ? (discountAmount / grossAmount) * 100 : 0;
        const res = await discountService.validateDiscount({
          employeeId: user?.employeeId || 1,
          centreId: centreId || 1,
          grossAmount,
          discountAmount,
          discountPercentage: pct,
          reasonId: selectedReasonId && selectedReasonId !== 'CUSTOM' ? parseInt(selectedReasonId, 10) : undefined,
          reasonName: discountReason || 'Concession',
          patientId: selectedPatientUhid || undefined
        });

        if (res.success && res.data) {
          setDiscountValidation(res.data);
          if (res.data.canSelfApprove) {
            setApprovalRequest((prev) => prev.status === 'APPROVED' ? prev : { ...prev, status: 'IDLE' });
          }
        }
      } catch (err) {
        console.error('Discount authority check error:', err);
      } finally {
        setIsValidatingDiscount(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [discountAmount, grossAmount, centreId, user, selectedReasonId, discountReason, selectedPatientUhid]);

  const handleSendDiscountForApproval = async () => {
    if (!discountReason) {
      return showToast('Please select a Concession Reason first.', 'error');
    }
    const currentReasonObj = discountReasons.find((r) => String(r.reasonId) === String(selectedReasonId));
    if ((currentReasonObj?.requiresRemarks || discountReason.toUpperCase() === 'OTHER' || selectedReasonId === 'CUSTOM') && !discountRemarks?.trim()) {
      return showToast('Remarks are mandatory for this concession reason.', 'error');
    }

    setApprovalRequest((prev) => ({ ...prev, submitting: true }));
    try {
      const pct = grossAmount > 0 ? (discountAmount / grossAmount) * 100 : 0;
      const res = await discountService.createApprovalRequest({
        patientId: selectedPatientUhid || 'WALKIN',
        patientName: patientData.pName ? `${patientData.title} ${patientData.pName}` : 'Walk-in Patient',
        centreId: centreId || 1,
        grossAmount,
        discountPercentage: pct,
        discountAmount,
        proposedNetAmount: netAmount,
        reasonId: selectedReasonId && selectedReasonId !== 'CUSTOM' ? parseInt(selectedReasonId, 10) : undefined,
        reasonName: discountReason,
        remarks: discountRemarks || undefined,
        cartItemsJson: JSON.stringify(selectedTests.map((t) => ({ name: t.itemName, rate: t.rate, quantity: t.quantity })))
      });

      if (res.success && res.data) {
        setApprovalRequest({
          status: 'PENDING',
          requestId: res.data.requestId,
          requestNo: res.data.requestNo,
          approvedByName: '',
          rejectionReason: '',
          submitting: false
        });
        showToast(`Discount Approval Request #${res.data.requestNo} created and sent to Approver Queue!`, 'success');
      } else {
        showToast(res.message || 'Failed to submit discount approval request.', 'error');
        setApprovalRequest((prev) => ({ ...prev, submitting: false }));
      }
    } catch (err) {
      console.error('Failed to submit approval request:', err);
      showToast(err.message || 'Failed to submit discount approval request.', 'error');
      setApprovalRequest((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleCheckApprovalStatus = async () => {
    if (!approvalRequest.requestId) return;
    try {
      const res = await discountService.getApprovalDetail(approvalRequest.requestId);
      if (res.success && res.data) {
        if (res.data.status === 'Approved' || res.data.status === 'Auto Approved') {
          // IMPORTANT: If Admin counter-offered a different amount, apply it!
          let counterMsg = '';
          if (res.data.approvedDiscountAmount != null && discountType === 'Amount' && res.data.approvedDiscountAmount !== discountOnTotal) {
            counterMsg = `Note: You requested ₹${discountAmount}, but the Admin approved ₹${res.data.approvedDiscountAmount}.`;
            setDiscountOnTotal(res.data.approvedDiscountAmount);
            showToast(counterMsg, 'info');
          }

          setApprovalRequest((prev) => ({
            ...prev,
            status: 'APPROVED',
            approvedByName: res.data.approvedByName || 'Authorized Approver',
            counterOfferMessage: counterMsg
          }));
          showToast(`Approval Request #${res.data.requestNo} is APPROVED by ${res.data.approvedByName || 'Approver'}!`, 'success');
        } else if (res.data.status === 'Rejected') {
          setApprovalRequest((prev) => ({
            ...prev,
            status: 'REJECTED',
            rejectionReason: res.data.rejectionReason || 'Rejected by approver',
            counterOfferMessage: ''
          }));
          showToast(`Approval Request #${res.data.requestNo} was REJECTED: ${res.data.rejectionReason}`, 'error');
        } else {
          showToast(`Request #${res.data.requestNo} is currently ${res.data.status}.`, 'info');
        }
      }
    } catch (err) {
      showToast('Could not fetch approval status.', 'error');
    }
  };

  // Multi-Payment Breakdown Entries State
  const [paymentsList, setPaymentsList] = useState([]);
  const [currentPaymentMode, setCurrentPaymentMode] = useState('Cash');
  const [currentPayAmount, setCurrentPayAmount] = useState('');
  const [isPayAmountManuallyEdited, setIsPayAmountManuallyEdited] = useState(false);
  const [cashReceived, setCashReceived] = useState('');

  // Mode Specific Conditional Fields for active payment input
  const [cardLast4, setCardLast4] = useState('');
  const [cardMachineRef, setCardMachineRef] = useState('');
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeBank, setChequeBank] = useState('');
  const [chequeDateVal, setChequeDateVal] = useState('');
  const [upiUtr, setUpiUtr] = useState('');

  const totalPaid = useMemo(() => {
    const sum = paymentsList.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
    return roundMoney(sum);
  }, [paymentsList]);

  // A B2B client's credit terms against this bill, shown before saving. The server applies them on save (B2b:CreditEnforcement).
  useEffect(() => {
    if (!partyId || !(netAmount > 0)) { setPartyGate(null); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      Promise.resolve(b2bService.getRegistrationGate(partyId, netAmount))
        .then((g) => { if (!cancelled) setPartyGate(g && g.violates ? g : null); })
        .catch(() => { if (!cancelled) setPartyGate(null); });
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [partyId, netAmount]);

  const remainingBalance = useMemo(() => {
    return roundMoney(Math.max(0, netAmount - totalPaid));
  }, [netAmount, totalPaid]);

  const isOverpaid = totalPaid > netAmount + 0.001;
  const overpaymentAmount = isOverpaid ? roundMoney(totalPaid - netAmount) : 0;

  // Cashier-first: quick cash denominations suggestions
  // Cashier calculation assistant: instant change / underpayment
  // Auto pre-fill currentPayAmount with remaining balance when available
  useEffect(() => {
    if (remainingBalance > 0) {
      if (!isPayAmountManuallyEdited || !currentPayAmount || parseFloat(currentPayAmount) > remainingBalance) {
        setCurrentPayAmount(remainingBalance.toFixed(2));
        if (parseFloat(currentPayAmount) > remainingBalance) {
          setIsPayAmountManuallyEdited(false);
        }
      }
    } else {
      setCurrentPayAmount('');
      setIsPayAmountManuallyEdited(false);
    }
  }, [remainingBalance, isPayAmountManuallyEdited]);

  // When cart tests or discount settings change, reset manual override so currentPayAmount matches the new bill
  useEffect(() => {
    setIsPayAmountManuallyEdited(false);
  }, [selectedTests, discountOnTotal, discountType]);

  const handleAddPayment = () => {
    const amt = roundMoney(parseFloat(currentPayAmount));
    if (isNaN(amt) || amt <= 0) {
      return showToast('Please enter a payment amount greater than 0.', 'error');
    }
    if (amt > remainingBalance + 0.001) {
      return showToast(`Payment amount cannot exceed remaining balance of ₹${remainingBalance.toFixed(2)}.`, 'error');
    }

    let refText = '—';
    if (currentPaymentMode.includes('Cash') || currentPaymentMode === 'Cash') {
      const recd = parseFloat(cashReceived);
      if (!isNaN(recd) && recd > amt) {
        refText = `Cash (Recd: ₹${recd.toFixed(2)}, Change: ₹${(recd - amt).toFixed(2)})`;
      } else if (!isNaN(recd) && recd > 0) {
        refText = `Cash (Recd: ₹${recd.toFixed(2)})`;
      } else {
        refText = 'Cash Counter';
      }
    } else if (currentPaymentMode === 'Credit / Debit Card') {
      if (cardLast4) refText = `Card: •••• ${cardLast4}${cardMachineRef ? ` (${cardMachineRef})` : ''}`;
      else refText = 'Card POS';
    } else if (currentPaymentMode === 'UPI / Online Payment') {
      if (upiUtr) refText = `UTR: ${upiUtr}`;
      else refText = 'UPI / Online';
    } else if (currentPaymentMode === 'Cheque / DD') {
      if (chequeNumber) refText = `Chq #${chequeNumber}${chequeBank ? `, ${chequeBank}` : ''}`;
      else refText = 'Cheque / DD';
    } else if (currentPaymentMode === 'Credit / Panel Sponsor') {
      refText = 'Corporate Panel Credit';
    }

    const newPayment = {
      id: `pay-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      paymentMode: currentPaymentMode,
      amount: amt,
      reference: refText,
      cardNo: cardLast4 || undefined,
      bankName: chequeBank || undefined,
      chequeNo: chequeNumber || undefined,
      chequeDate: chequeDateVal || undefined,
      transactionId: currentPaymentMode === 'UPI / Online Payment' ? upiUtr : cardMachineRef || undefined,
    };

    setPaymentsList((prev) => [...prev, newPayment]);
    setCurrentPaymentMode('');
    setCurrentPayAmount('');
    setIsPayAmountManuallyEdited(false);
    setCashReceived('');
    setCardLast4('');
    setCardMachineRef('');
    setUpiUtr('');
    setChequeNumber('');
    setChequeBank('');
    setChequeDateVal('');
  };

  const handleRemovePayment = (id) => {
    setPaymentsList((prev) => prev.filter((p) => p.id !== id));
    setIsPayAmountManuallyEdited(false);
  };

  // Demographic & Clinical completion check
  // Overall Form Validation & Readiness
  const { isFormReady, missingRequirementsText } = useMemo(() => {
    const missing = [];

    // Patient Full Name (demographics)
    if (isFieldRequired('patient.firstName', true) && !patientData.pName.trim()) {
      missing.push(getFieldLabel('patient.firstName', 'Patient Name'));
    }

    // Gender (demographics)
    if (isFieldRequired('patient.gender', true) && !patientData.gender) {
      missing.push(getFieldLabel('patient.gender', 'Gender'));
    }

    // Mobile Number (contact)
    if (isFieldRequired('patient.mobile', true)) {
      if (!patientData.mobile || patientData.mobile.length !== 10) {
        missing.push(getFieldLabel('patient.mobile', '10-digit Mobile Number'));
      }
    } else if (patientData.mobile && patientData.mobile.length !== 10) {
      missing.push('Valid 10-digit Mobile Number (or leave blank)');
    }

    // Date of Birth / Age (Either DOB or Age satisfies requirement)
    const isAgeEmptyForm = (patientData.ageYear === '' || patientData.ageYear === null || patientData.ageYear === undefined) &&
                           (patientData.ageMonth === '' || patientData.ageMonth === null || patientData.ageMonth === undefined) &&
                           (patientData.ageDays === '' || patientData.ageDays === null || patientData.ageDays === undefined);
    if (!patientData.dob && isAgeEmptyForm) {
      if (isFieldRequired('patient.dateOfBirth', false) || isFieldRequired('patient.age', true)) {
        missing.push('Patient Age or Date of Birth');
      }
    }
    const ageErr = getAgeValidationError(patientData.ageYear);
    if (ageErr) {
      missing.push(ageErr);
    }

    // Other Demographic fields
    if (isFieldRequired('patient.title') && !patientData.title) {
      missing.push(getFieldLabel('patient.title', 'Title'));
    }
    if (isFieldRequired('patient.bloodGroup') && !patientData.bloodGroup) {
      missing.push(getFieldLabel('patient.bloodGroup', 'Blood Group'));
    }
    if (isFieldRequired('patient.weight') && !patientData.weight) {
      missing.push(getFieldLabel('patient.weight', 'Weight'));
    }
    if (isFieldRequired('patient.height') && !patientData.height) {
      missing.push(getFieldLabel('patient.height', 'Height'));
    }
    if (isFieldRequired('patient.lmp') && patientData.gender === 'Female' && !patientData.lmp) {
      missing.push(getFieldLabel('patient.lmp', 'LMP'));
    }
    if (isFieldRequired('patient.maritalStatus') && !patientData.maritalStatus) {
      missing.push(getFieldLabel('patient.maritalStatus', 'Marital Status'));
    }
    if (isFieldRequired('patient.nationality') && !patientData.nationality) {
      missing.push(getFieldLabel('patient.nationality', 'Nationality'));
    }
    if (isFieldRequired('patient.category') && !patientData.patientCategory) {
      missing.push(getFieldLabel('patient.category', 'Patient Category'));
    }

    // Contact fields
    if (isFieldRequired('patient.email') && !patientData.email) {
      missing.push(getFieldLabel('patient.email', 'Email Address'));
    }
    if (isFieldRequired('patient.alternateMobile') && !patientData.alternateMobile) {
      missing.push(getFieldLabel('patient.alternateMobile', 'Alternate Mobile'));
    }

    // Address fields
    if (isFieldRequired('patient.addressLine1') && !patientData.address) {
      missing.push(getFieldLabel('patient.addressLine1', 'Address'));
    }
    if (isFieldRequired('patient.city') && !patientData.city) {
      missing.push(getFieldLabel('patient.city', 'City'));
    }
    if (isFieldRequired('patient.state') && !patientData.state) {
      missing.push(getFieldLabel('patient.state', 'State'));
    }
    if (isFieldRequired('patient.pincode') && !patientData.pincode) {
      missing.push(getFieldLabel('patient.pincode', 'PIN Code'));
    }

    // Guardian fields
    if (isFieldRequired('patient.guardianName') && !patientData.guardianName) {
      missing.push(getFieldLabel('patient.guardianName', 'Guardian Name'));
    }
    if (isFieldRequired('patient.guardianRelation') && !patientData.guardianRelation) {
      missing.push(getFieldLabel('patient.guardianRelation', 'Guardian Relation'));
    }
    if (isFieldRequired('patient.guardianMobile') && !patientData.guardianMobile) {
      missing.push(getFieldLabel('patient.guardianMobile', 'Guardian Mobile'));
    }

    // Government ID fields
    if (isFieldRequired('patient.governmentIdType') && !patientData.governmentIdType) {
      missing.push(getFieldLabel('patient.governmentIdType', 'ID Proof Type'));
    }
    if (isFieldRequired('patient.governmentIdNumber') && !patientData.governmentIdNumber) {
      missing.push(getFieldLabel('patient.governmentIdNumber', 'ID Proof Number'));
    }
    if (isFieldRequired('patient.abha') && !patientData.abhaId) {
      missing.push(getFieldLabel('patient.abha', 'ABHA Health ID'));
    }

    // Referral & Encounter fields
    if (isFieldRequired('referral.source') && !referralSourceId) {
      missing.push(getFieldLabel('referral.source', 'Referral Source'));
    }
    if (isFieldRequired('referral.referringDoctor') && !doctorId) {
      missing.push(getFieldLabel('referral.referringDoctor', 'Referring Doctor'));
    }
    if (isFieldRequired('referral.commissionBeneficiary') && !beneficiaryId) {
      missing.push(getFieldLabel('referral.commissionBeneficiary', 'Commission Beneficiary'));
    }
    if (isFieldRequired('registration.panel') && !panelId) {
      missing.push(getFieldLabel('registration.panel', 'Billing Panel'));
    }
    if (isFieldRequired('registration.visitType') && !visitType) {
      missing.push(getFieldLabel('registration.visitType', 'Visit Type'));
    }
    if (isFieldRequired('registration.patientType') && !patientType) {
      missing.push(getFieldLabel('registration.patientType', 'Patient Classification'));
    }
    if (isFieldRequired('referral.referringLab') && !referringLab) {
      missing.push(getFieldLabel('referral.referringLab', 'Referring Lab'));
    }
    if (isFieldRequired('referral.pro') && !proName) {
      missing.push(getFieldLabel('referral.pro', 'PRO Executive'));
    }
    if (isFieldRequired('referral.phlebotomist') && !phlebotomistName) {
      missing.push(getFieldLabel('referral.phlebotomist', 'Assigned Phlebotomist'));
    }
    if (isFieldRequired('registration.scheduledCollection') && !collectionDate) {
      missing.push(getFieldLabel('registration.scheduledCollection', 'Scheduled Collection Date'));
    }
    if ((isFieldRequired('registration.srfId') || isFieldRequired('patient.srfId')) && !srfId) {
      missing.push(getFieldLabel('registration.srfId', 'SRF ID'));
    }

    // Clinical Information
    if (isFieldRequired('clinical.history') && !patientData.clinicalHistory) {
      missing.push(getFieldLabel('clinical.history', 'Clinical History'));
    }

    // Operational requirements
    if (selectedTests.length === 0) missing.push('At least one investigation in cart');
    if (discountAmount > 0 && !discountReason.trim()) missing.push('Discount Reason');
    if (discountAmount > 0 && !discountValidation?.canSelfApprove && approvalRequest.status !== 'APPROVED') {
      missing.push('Managerial Discount Approval');
    }
    if (isOverpaid) missing.push(`Total Paid exceeds Net Amount by ₹${overpaymentAmount.toFixed(2)}`);

    return {
      isFormReady: missing.length === 0,
      missingRequirementsText: missing.length > 0 ? `Please complete: ${missing.join(', ')}` : '',
    };
  }, [
    patientData,
    dobMode,
    isFieldRequired,
    getFieldLabel,
    doctorId,
    referralSourceId,
    beneficiaryId,
    panelId,
    visitType,
    patientType,
    referringLab,
    proName,
    phlebotomistName,
    collectionDate,
    srfId,
    selectedTests.length,
    discountAmount,
    discountReason,
    discountValidation?.canSelfApprove,
    approvalRequest.status,
    isOverpaid,
    overpaymentAmount,
  ]);

  // =========================================================================
  // 7.5. DRAFTS QUEUE MANAGEMENT
  // =========================================================================
  const [drafts, setDrafts] = useState([]);
  const [isDraftsModalOpen, setIsDraftsModalOpen] = useState(false);
  const [, setDraftsLoading] = useState(false);

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    try {
      const res = await draftService.getDrafts();
      if (res && res.success) {
        setDrafts(res.data || []);
      }
    } catch (err) {
      console.error('Failed to load drafts:', err);
    }
  };

  const handleSaveDraft = async () => {
    if (!patientData.pName && selectedTests.length === 0) {
      return showToast('Nothing to draft. Add patient details or tests first.', 'info');
    }
    setDraftsLoading(true);
    try {
      const newDraftData = {
        patientData,
        selectedTests,
        paymentsList,
        panelId,
        partyId,
        contractId,
        clientOrderNumber,
        discountOnTotal,
        discountType,
        discountReason,
        selectedReasonId,
        discountRemarks,
        approvalRequest,
        discountValidation,
      };
      await draftService.saveDraft(newDraftData);
      showToast('Session saved to Drafts.', 'success');
      handleResetToNewPatient();
      await fetchDrafts();
    } catch (err) {
      showToast('Failed to save draft.', 'error');
    } finally {
      setDraftsLoading(false);
    }
  };

  const handleResumeDraft = async (draft) => {
    const data = draft.data || {};
    setPatientData(data.patientData || {});
    setSelectedTests(data.selectedTests || []);
    setPaymentsList(data.partyId ? [] : (data.paymentsList || []));
    if (data.panelId) setPanelId(data.panelId);
    if (data.partyId) {
      setPartyId(data.partyId);
      setContractId(data.contractId || '');
      setClientOrderNumber(data.clientOrderNumber || '');
      b2bService.validateEligibility(data.partyId).then(setPartyCreditStatus).catch(() => {});
    } else {
      setPartyId('');
      setContractId('');
      setClientOrderNumber('');
      setPartyCreditStatus(null);
    }
    setDiscountOnTotal(data.discountOnTotal || 0);
    setDiscountType(data.discountType || 'Amount');
    setDiscountReason(data.discountReason || '');
    setSelectedReasonId(data.selectedReasonId || '');
    setDiscountRemarks(data.discountRemarks || '');
    setApprovalRequest(data.approvalRequest || { status: null, requestId: null, levelNumber: 1 });
    setDiscountValidation(data.discountValidation || null);
    
    setIsDraftsModalOpen(false);
    showToast('Draft resumed successfully.', 'success');
    
    // Optionally delete from database after resuming to prevent duplicate resuming
    try {
      await draftService.deleteDraft(draft.id);
      setDrafts(prev => prev.filter(d => d.id !== draft.id));
    } catch (e) {
      console.error('Could not auto-delete draft after resume', e);
    }
  };

  const handleDeleteDraft = async (id) => {
    try {
      await draftService.deleteDraft(id);
      setDrafts(prev => prev.filter(d => d.id !== id));
      showToast('Draft deleted.', 'success');
    } catch (err) {
      showToast('Failed to delete draft.', 'error');
    }
  };

  // =========================================================================
  // 8. REGISTRATION COMMIT & RECEIPT MODAL
  // =========================================================================
  const [submitting, setSubmitting] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  const handleRegister = async () => {
    // Synchronous immediate lock to reject rapid double-clicks / Enter hammering
    if (isSubmittingRef.current || submitting) {
      console.warn('Registration already in progress. Ignoring duplicate click/enter.');
      return;
    }

    if (!isFormReady) {
      return showToast(missingRequirementsText || 'Please complete all required fields.', 'error');
    }

    if (isOverpaid) {
      return showToast(`Total Paid (₹${totalPaid.toFixed(2)}) cannot exceed Net Payable (₹${netAmount.toFixed(2)}). Please reduce discount or adjust payments.`, 'error');
    }

    if (collectionCharge.error) {
      return showToast('Please correct the collection charge entry before saving.', 'error');
    }

    if (discountAmount > 0) {
      if (!discountReason) {
        return showToast('Please select or specify a Concession Reason.', 'error');
      }
      const currentReasonObj = discountReasons.find((r) => String(r.reasonId) === String(selectedReasonId));
      if ((currentReasonObj?.requiresRemarks || discountReason.toUpperCase() === 'OTHER' || selectedReasonId === 'CUSTOM') && !discountRemarks?.trim()) {
        return showToast('Remarks are mandatory for this concession reason.', 'error');
      }
      if (!discountValidation?.canSelfApprove && approvalRequest.status !== 'APPROVED') {
        return showToast('This discount requires managerial approval. Please click "Send for Approval" before registering.', 'error');
      }
    }

    const careCoinsPointsNum = parseInt(careCoinsPointsToRedeem, 10);
    if (careCoinsPointsNum > 0) {
      if (isPreviewingCareCoins) {
        return showToast('Still checking the CareCoins redemption — please wait a moment and try again.', 'error');
      }
      if (careCoinsPreviewError || !careCoinsPreview?.isEligible) {
        return showToast(careCoinsPreviewError || 'CareCoins redemption could not be validated. Adjust the points or remove the redemption before registering.', 'error');
      }
      if (discountAmount > 0 && careCoinsPreview.allowStackingWithDiscount === false) {
        return showToast('This CareCoins rule does not allow combining a redemption with a manual discount on the same bill. Remove one of them.', 'error');
      }
    }

    isSubmittingRef.current = true;
    setSubmitting(true);
    try {
      const idempotencyKey = idempotencyKeyRef.current;

      const formattedPayments = paymentsList.map((p) => ({
        amount: p.amount,
        paymentMode: p.paymentMode,
        cardNo: p.cardNo,
        bankName: p.bankName,
        chequeNo: p.chequeNo,
        chequeDate: p.chequeDate,
        transactionId: p.transactionId,
        narration: `Registration Settlement (${p.paymentMode}) for ${patientData.pName}`,
      }));

      const primarySettlement = formattedPayments.length > 0
        ? formattedPayments[0]
        : {
            amount: 0,
            paymentMode: 'Credit',
            narration: `Due Registration for ${patientData.pName}`,
          };

      const payload = {
        patientId: selectedPatientUhid || undefined,
        title: patientData.title,
        pName: patientData.pName.trim().toUpperCase(),
        gender: patientData.gender,
        dob: patientData.dob || undefined,
        isDobActual: patientData.isDobActual,
        ageYear: (patientData.ageYear !== '' && patientData.ageYear !== null && patientData.ageYear !== undefined && !isNaN(parseInt(patientData.ageYear, 10)))
          ? parseInt(patientData.ageYear, 10)
          : undefined,
        ageMonth: (patientData.ageMonth !== '' && patientData.ageMonth !== null && patientData.ageMonth !== undefined && !isNaN(parseInt(patientData.ageMonth, 10)))
          ? parseInt(patientData.ageMonth, 10)
          : undefined,
        ageDays: (patientData.ageDays !== '' && patientData.ageDays !== null && patientData.ageDays !== undefined && !isNaN(parseInt(patientData.ageDays, 10)))
          ? parseInt(patientData.ageDays, 10)
          : undefined,
        mobile: patientData.mobile,
        email: patientData.email || undefined,
        address: patientData.address || undefined,
        city: patientData.city || undefined,
        state: patientData.state || undefined,
        pincode: patientData.pincode || undefined,
        clinicalHistory: patientData.clinicalHistory || undefined,
        bloodGroup: patientData.bloodGroup || undefined,
        weight: patientData.weight ? parseFloat(patientData.weight) : undefined,
        weightUnit: patientData.weightUnit || 'kg',
        height: patientData.height ? parseFloat(patientData.height) : undefined,
        heightUnit: patientData.heightUnit || 'cm',
        lmp: patientData.lmp || undefined,
        guardianName: patientData.guardianName || undefined,
        guardianRelation: patientData.guardianRelation || undefined,
        guardianMobile: patientData.guardianMobile || undefined,
        governmentIdType: patientData.governmentIdType || undefined,
        governmentIdNumber: patientData.governmentIdNumber || undefined,
        abhaId: patientData.abhaId || undefined,
        photoUrl: patientPhotoUrl || undefined,
        centreId: parseInt(centreId, 10),
        panelId: parseInt(panelId, 10),
        partyId: partyId ? parseInt(partyId, 10) : undefined,
        contractId: contractId ? parseInt(contractId, 10) : undefined,
        isB2BOrder: !!partyId,
        clientOrderNumber: clientOrderNumber || undefined,
        referralSourceId: referralSourceId ? parseInt(referralSourceId, 10) : undefined,
        referralSourceName: referralSourceName || undefined,
        beneficiaryType: beneficiaryType || undefined,
        beneficiaryId: beneficiaryId ? parseInt(beneficiaryId, 10) : undefined,
        beneficiaryName: beneficiaryName || undefined,
        doctorId: doctorId ? parseInt(doctorId, 10) : undefined,
        doctorName: doctorName || undefined,
        proName: proName || undefined,
        phlebotomistName: phlebotomistName || undefined,
        visitType: visitType || 'Center Visit',
        srfId: srfId || undefined,
        priority: priority,
        isUrgent: priority === 'Urgent',
        autoCollectSample: !!autoCollectSample,
        autoReceiveSample: !!autoReceiveSample,
        documentIds: patientDocuments.map((d) => d.documentId),
        discountOnTotal: discountAmount,
        discountReason: discountReason || undefined,
        discountReasonId: selectedReasonId && selectedReasonId !== 'CUSTOM' ? parseInt(selectedReasonId, 10) : undefined,
        discountRemarks: discountRemarks || undefined,
        discountStatus: approvalRequest.status === 'APPROVED' ? 'Approved' : (discountValidation?.canSelfApprove ? 'Self Approved' : 'None'),
        discountApprovalRequestId: approvalRequest.requestId || undefined,
        isCredit: isCreditBill({ payments: paymentsList, partyId, remainingBalance }),
        idempotencyKey: idempotencyKey,
        appointmentId: linkedAppointment?.appointmentId || undefined,
        // CareCoins redemption — reuses this same registration idempotencyKey (the backend
        // DTO carries no separate CareCoinsIdempotencyKey field; see implementation report).
        careCoinsRedeemPoints: careCoinsPointsNum > 0 ? careCoinsPointsNum : undefined,
        // Only what the cashier entered; the server resolves the rule and calculates the amount itself.
        collectionCharge: collectionCharge.entry || undefined,
        tests: selectedTests.map((t) => ({
          investigationId: t.investigationId,
          itemId: t.itemId,
          itemCode: t.itemCode,
          itemName: t.itemName,
          // Only used by the server to look the price up; the rate below is shown to the cashier, the bill uses the server's price.
          itemType: t.itemType || 'TEST',
          subCategoryId: t.subCategoryId,
          subCategoryName: t.subCategoryName,
          rate: t.rate,
          contractRate: partyId ? t.rate : undefined,
          mrpRate: t.rate,
          rateSource: partyId ? 'CONTRACT' : 'PANEL',
          partyId: partyId ? parseInt(partyId, 10) : undefined,
          contractId: contractId ? parseInt(contractId, 10) : undefined,
          discountAmt: 0,
          quantity: 1,
          sampleTypeId: t.sampleTypeId,
          sampleTypeName: t.sampleTypeName,
          containerId: t.containerId,
          reportType: t.reportType,
          isUrgent: priority === 'Urgent' || !!t.isUrgent,
        })),
        payment: primarySettlement,
        settlement: primarySettlement,
        payments: formattedPayments,
      };

      const res = await registrationService.createRegistration(payload);
      if (res.success && res.data) {
        showToast(`Registration Successful! LabNo: ${res.data.ledgerTransactionNo}`);

        // Persist newly captured/uploaded photo for new registration
        if (patientPhotoFile && res.data.patientId) {
          try {
            const photoFormData = new FormData();
            photoFormData.append('file', patientPhotoFile);
            photoFormData.append('patientId', res.data.patientId);
            await patientDocumentService.uploadPhoto(photoFormData);
          } catch (photoErr) {
            console.error('Failed to persist patient photo post-registration:', photoErr);
          }
        }

        // Persist biometric face template if captured
        if (enrolledFaceVector && res.data.patientId) {
          try {
            await faceAuthService.enrollPatientFace(res.data.patientId, enrolledFaceVector);
            console.info('[Registration] Persisted face biometric for new patient:', res.data.patientId);
          } catch (fErr) {
            console.warn('Failed to persist patient face biometric post-registration:', fErr);
          }
        }

        // Fetch receipt and open receipt modal
        try {
          const rcptRes = await receiptService.getReceiptByLedgerNo(res.data.ledgerTransactionNo);
          if (rcptRes.success && rcptRes.data) {
            setReceiptData(rcptRes.data);
            setIsReceiptModalOpen(true);
          }
        } catch (rcptErr) {
          console.error('Failed to load receipt:', rcptErr);
        }

        // Automatic TRF (Radiology requisition). Runs only after the registration is saved; the
        // server applies TRF enablement / auto-print / mode, so this is a no-op for Pathology-only
        // registrations. A failure here never affects the saved registration or payment.
        try {
          const trfOutcome = await runTrfPrint(res.data.ledgerTransactionNo, 'AUTO');
          const trfMsg = describeTrfOutcome(trfOutcome, 'AUTO');
          if (trfMsg) showToast(trfMsg.message, trfMsg.type === 'error' ? 'error' : 'success');
        } catch (trfErr) {
          console.error('TRF auto-print error:', trfErr);
          showToast('TRF could not be printed automatically. Print it from the receipt.', 'error');
        }

        // The appointment (if any) is now REGISTERED on the server; drop the link so the next patient starts clean.
        if (linkedAppointment) {
          setLinkedAppointment(null);
          loadedAppointmentRef.current = null;
          navigate('/registration', { replace: true });
        }

        // Auto-reset entire screen immediately for the next patient
        handleResetToNewPatient();
      } else {
        showToast(res.message || 'Registration failed.', 'error');
      }
    } catch (err) {
      console.error('Registration commit error:', err);
      const msg = err.response?.data?.message || err.message || 'Registration failed.';
      showToast(msg, 'error');
    } finally {
      isSubmittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="futuristic-lims-workbench">
      {/* Toast Alert */}
      {toast && (
        <div
          className={`futuristic-toast ${toast.type === 'error' ? 'toast-error' : 'toast-success'}`}
          role="alert"
        >
          <span className="toast-icon">{toast.type === 'error' ? '✕' : '✓'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      {(linkedAppointment || appointmentLinkError) && (
        <div className={`apt-reg-banner ${appointmentLinkError ? 'error' : ''}`} role="status">
          <span>
            {appointmentLinkError
              ? appointmentLinkError
              : <>Registering from appointment <strong>{linkedAppointment.appointmentNo}</strong>{linkedAppointment.tokenNo ? <> (token <strong>#{linkedAppointment.tokenNo}</strong>)</> : null}. It is marked Registered when this bill is saved.</>}
          </span>
          <button type="button" onClick={clearAppointmentLink}>
            {appointmentLinkError ? 'Dismiss' : 'Not this appointment (register as walk-in)'}
          </button>
        </div>
      )}

      {!linkedAppointment && todaysAppointments.filter((x) => !dismissedAppointmentIds.includes(x.appointmentId)).length > 0 && (
        <div className="apt-reg-banner" role="status">
          <span>
            <strong>This patient has an appointment today.</strong>{' '}
            {todaysAppointments.filter((x) => !dismissedAppointmentIds.includes(x.appointmentId)).map((x) => (
              <span key={x.appointmentId} style={{ marginRight: 12 }}>
                {x.appointmentNo} at {x.slotStart} ({x.status === 'CONFIRMED' ? 'confirmed' : 'booked'}){x.tokenNo ? `, token #${x.tokenNo}` : ''}{' '}
                <button type="button" onClick={() => registerFromTodaysAppointment(x)}>Register from this appointment</button>
              </span>
            ))}
          </span>
          <button type="button" onClick={() => setDismissedAppointmentIds((ids) => [...ids, ...todaysAppointments.map((x) => x.appointmentId)])}>Register as walk-in</button>
        </div>
      )}

      {/* ===================================================================== */}
      {/* 1. TOP APP WORKBENCH CONTROL BAR                                      */}
      {/* ===================================================================== */}
      <div className="workbench-top-bar">
        <div className="top-bar-left">
          <div className="workbench-breadcrumb">
            <span className="bc-home">🏠</span>
            <span className="bc-module">Registration & Billing</span>
            <span className="bc-sep">›</span>
            <h1 className="workbench-heading">Patient Registration</h1>
          </div>
          {(lookupsLoading || configLoading) && (
            <span style={{ fontSize: '0.72rem', color: 'var(--accent, #0284c7)', fontWeight: 600, marginLeft: '10px' }}>
              ⚡ Syncing...
            </span>
          )}
        </div>

        <div className="top-bar-right">
          {/* Order Priority Selector */}
          <div className="order-priority-toggle-wrap">
            <span className="priority-label-sm">PRIORITY:</span>
            <div className="priority-pill-group">
              <button
                type="button"
                className={`priority-pill-btn ${priority === 'Routine' ? 'active-routine' : ''}`}
                onClick={() => setPriority('Routine')}
                title="Normal processing turnaround time"
              >
                Routine
              </button>
              <button
                type="button"
                className={`priority-pill-btn ${priority === 'Urgent' ? 'active-urgent' : ''}`}
                onClick={() => setPriority('Urgent')}
                title="Fast-track STAT processing priority"
              >
                🔴 Urgent (STAT)
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ===================================================================== */}
      {/* UNIFIED SINGLE-SCREEN WORKBENCH: REGISTRATION & BILLING               */}
      {/* ===================================================================== */}
      <div className="workbench-unified-content animate-fade-in">

        {/* Unified Patient Identification & Search Bar (Single-Screen Workflow) */}
        <div className="command-search-section animate-fade-in">
          <div className="command-search-wrapper">
            <span className="command-search-icon">🔍</span>
            <input
              ref={patientSearchInputRef}
              type="text"
              className="command-search-input"
              placeholder="Search patient by UHID, Mobile Number, Full Name, or Registration No..."
              value={patientSearchQuery}
              onChange={(e) => setPatientSearchQuery(e.target.value)}
              id="input-patient-search"
            />
            {isSearchingPatient && (
              <div className="command-search-loading">Searching...</div>
            )}
            {patientSearchQuery && (
              <button
                type="button"
                className="command-search-clear"
                onClick={() => {
                  setPatientSearchQuery('');
                  setPatientSearchResults([]);
                }}
                title="Clear search"
              >
                ✕
              </button>
            )}

            {/* Identify by Face Scan Button */}
            <button
              type="button"
              className="command-search-face-btn"
              onClick={() => setIsFaceScanModalOpen(true)}
              title="Identify Patient by Face Scan"
              id="btn-patient-face-scan"
            >
              <span>👤</span>
              <span>Face ID</span>
            </button>

            {/* Dynamic Results Dropdown */}
            {patientSearchResults.length > 0 && (
              <div className="command-search-dropdown">
                <div className="search-dropdown-header">
                  <span>Select patient record ({patientSearchResults.length} matches):</span>
                  <span>Press ESC to close</span>
                </div>
                {patientSearchResults.map((p) => (
                  <div
                    key={p.patientId}
                    className="search-result-row"
                    onClick={() => handleSelectExistingPatient(p)}
                  >
                    <div className="res-avatar-icon">
                      {p.gender === 'Female' ? '👩' : '👨'}
                    </div>
                    <div className="res-info">
                      <div className="res-name-row">
                        <strong className="res-name">{p.fullName || p.pName}</strong>
                        <span className="res-uhid-badge">{p.patientId}</span>
                      </div>
                      <div className="res-meta-line">
                        <span>{p.gender}</span>
                        <span>•</span>
                        <span>{p.ageString || `${p.ageYear || 0} Y`}</span>
                        <span>•</span>
                        <span>📞 {p.mobile}</span>
                        {p.city && (
                          <>
                            <span>•</span>
                            <span>📍 {p.city}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <button type="button" className="res-action-btn">
                      Use Patient →
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Compact Patient Identification Status Indicator */}
          {faceIdStatus !== 'idle' && (
            <div className={`patient-id-status-bar status-${faceIdStatus}`}>
              <div className="patient-id-status-content">
                {faceIdStatus === 'matched' && (
                  <>
                    <span className="status-icon">✓</span>
                    <div className="status-text-wrap">
                      <strong className="status-main-title">Registered Patient — Identity Matched</strong>
                      <span className="status-sub-desc">{faceIdStatusDetail || `UHID: ${selectedPatientUhid || patientData.patientId}`}</span>
                    </div>
                    <button
                      type="button"
                      className="status-action-link-btn"
                      onClick={handleResetToNewPatient}
                      title="Clear and start new registration"
                    >
                      Clear / New Registration
                    </button>
                  </>
                )}

                {faceIdStatus === 'unmatched' && (
                  <>
                    <span className="status-icon">ℹ️</span>
                    <div className="status-text-wrap">
                      <strong className="status-main-title">Patient Not Registered — Manual Check Available</strong>
                      <span className="status-sub-desc">
                        {faceIdStatusDetail || 'Face not found in registered patients. You can continue with manual verification or register the patient now.'}
                      </span>
                    </div>
                    <div className="status-action-btn-group">
                      <button
                        type="button"
                        className="status-action-pill-btn btn-verify-manual"
                        onClick={() => {
                          if (patientSearchInputRef.current) {
                            patientSearchInputRef.current.focus();
                          }
                        }}
                        title="Focus search input for manual verification"
                      >
                        🔍 Verify Manually
                      </button>
                      <button
                        type="button"
                        className="status-action-pill-btn btn-register-new"
                        onClick={() => {
                          setFaceIdStatus('idle');
                          setFaceIdStatusDetail('');
                        }}
                        title="Dismiss status and register new patient"
                      >
                        ➕ Register Patient
                      </button>
                    </div>
                  </>
                )}

                {faceIdStatus === 'error' && (
                  <>
                    <span className="status-icon">⚠️</span>
                    <div className="status-text-wrap">
                      <strong className="status-main-title">Face could not be verified — Try Again or Search Manually</strong>
                      <span className="status-sub-desc">{faceIdStatusDetail}</span>
                    </div>
                    <div className="status-action-btn-group">
                      <button
                        type="button"
                        className="status-action-pill-btn btn-retry-face"
                        onClick={() => setIsFaceScanModalOpen(true)}
                      >
                        🔄 Retry Face ID
                      </button>
                      <button
                        type="button"
                        className="status-action-pill-btn btn-verify-manual"
                        onClick={() => {
                          if (patientSearchInputRef.current) {
                            patientSearchInputRef.current.focus();
                          }
                        }}
                      >
                        🔍 Search Manually
                      </button>
                    </div>
                  </>
                )}

                {faceIdStatus === 'cancelled' && (
                  <>
                    <span className="status-icon">ℹ️</span>
                    <div className="status-text-wrap">
                      <strong className="status-main-title">Face identification cancelled</strong>
                      <span className="status-sub-desc">You can continue with manual patient search or enter details to register.</span>
                    </div>
                    <button
                      type="button"
                      className="status-action-link-btn"
                      onClick={() => {
                        setFaceIdStatus('idle');
                        setFaceIdStatusDetail('');
                      }}
                    >
                      Dismiss ✕
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {duplicateWarning && (
            <div className="inline-duplicate-alert">
              <span>⚠️ Potential Duplicate: A patient with mobile <strong>{patientData.mobile}</strong> and name <strong>{patientData.pName}</strong> already exists.</span>
            </div>
          )}
        </div>

          {/* ===================================================================== */}
          {/* SECTION 1: COMPACT PATIENT DEMOGRAPHICS & ENCOUNTER CONTEXT CARD       */}
          {/* ===================================================================== */}
          <div className="reg-compact-card">
            <div className="card-header-bar demographics-header-bar">
              <div className="demographics-header-left">
                <div className="card-title-icon-badge">
                  👥
                </div>
                <h3 className="card-title">Patient Demographics & Encounter Context</h3>
                {selectedPatientUhid && (
                  <span className="identity-reg-badge existing">
                    ● Existing: {selectedPatientUhid}
                  </span>
                )}
              </div>

              <div className="demographics-header-right-actions">
                <button
                  type="button"
                  className="btn-new-registration-green"
                  onClick={handleResetToNewPatient}
                  title="Start a fresh registration"
                >
                  + New Registration
                </button>

                <input
                  type="file"
                  ref={photoFileInputRef}
                  style={{ display: 'none' }}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoUpload}
                />

                {patientPhotoUrl ? (
                  <div className="header-photo-active-group">
                    <button
                      type="button"
                      className="btn-header-action has-photo"
                      onClick={() => photoFileInputRef.current?.click()}
                      disabled={photoUploading}
                      title="Click to replace photo"
                    >
                      <img src={patientPhotoUrl} alt="Patient" className="header-photo-thumb" />
                      <span>Photo ✓</span>
                    </button>
                    <button
                      type="button"
                      className="btn-header-action"
                      onClick={() => setIsCameraModalOpen(true)}
                      disabled={photoUploading}
                      title="Retake photo using camera"
                    >
                      📷 Retake
                    </button>
                    <button
                      type="button"
                      className="btn-photo-remove"
                      onClick={handleRemovePhoto}
                      title="Remove photo"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn-header-action"
                      onClick={() => photoFileInputRef.current?.click()}
                      disabled={photoUploading}
                      title="Upload patient photo from computer"
                    >
                      📷 Upload Photo
                    </button>
                    <button
                      type="button"
                      className="btn-header-action btn-camera-action"
                      onClick={() => setIsCameraModalOpen(true)}
                      disabled={photoUploading}
                      title="Capture photo using camera"
                    >
                      📷 Camera
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="card-body-content">
              <div className="reg-dense-grid-12">
                {/* ── ROW 1: Title (1), Name (4), Gender (2), Mobile (3), Email (2) ── */}
                {/* Title */}
                {isFieldVisible('patient.title') && (
                  <div className="grid-field span-1">
                    <label className="field-label">
                      {getFieldLabel('patient.title', 'Title')}
                      {isFieldRequired('patient.title') && <span className="req">*</span>}
                    </label>
                    <div className="field-control-with-icon">
                      <span className="field-prefix-icon-box">👤</span>
                      <select
                        className="field-select"
                        value={patientData.title}
                        disabled={isFieldReadOnly('patient.title')}
                        onChange={(e) => setPatientData({ ...patientData, title: e.target.value })}
                      >
                        {lookups.titles.map((t) => (
                          <option key={t.code} value={t.value}>
                            {t.value}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Patient Full Name */}
                {isFieldVisible('patient.firstName') && (
                  <div className="grid-field span-4">
                    <label className="field-label">
                      {getFieldLabel('patient.firstName', 'Patient Full Name')}
                      {isFieldRequired('patient.firstName', true) && <span className="req">*</span>}
                      {patientData.pName.trim().length > 0 && (
                        <span className="field-valid-mark">✓</span>
                      )}
                    </label>
                    <div className="field-control-with-icon">
                      <span className="field-prefix-icon-box">👤</span>
                      <input
                        type="text"
                        className="field-input text-bold"
                        placeholder={getFieldPlaceholder('patient.firstName', 'ENTER FULL NAME')}
                        value={patientData.pName}
                        readOnly={isFieldReadOnly('patient.firstName')}
                        onChange={(e) => setPatientData({ ...patientData, pName: e.target.value.toUpperCase() })}
                        onBlur={checkDuplicate}
                      />
                    </div>
                  </div>
                )}

                {/* Gender */}
                {isFieldVisible('patient.gender') && (
                  <div className="grid-field span-2">
                    <label className="field-label">
                      {getFieldLabel('patient.gender', 'Gender')}
                      {isFieldRequired('patient.gender', true) && <span className="req">*</span>}
                    </label>
                    <div className="field-control-with-icon">
                      <span className="field-prefix-icon-box">⚥</span>
                      <select
                        className="field-select"
                        value={patientData.gender}
                        disabled={isFieldReadOnly('patient.gender')}
                        onChange={(e) => setPatientData({ ...patientData, gender: e.target.value })}
                      >
                        {lookups.genders.map((g) => (
                          <option key={g.code} value={g.value}>
                            {g.value}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Primary Mobile */}
                {isFieldVisible('patient.mobile') && (
                  <div className="grid-field span-3">
                    <label className="field-label">
                      {getFieldLabel('patient.mobile', 'Primary Mobile')}
                      {isFieldRequired('patient.mobile', true) && <span className="req">*</span>}
                      {patientData.mobile.length === 10 && (
                        <span className="field-valid-mark">✓ 10-digits</span>
                      )}
                    </label>
                    <div className="field-control-with-icon">
                      <span className="field-prefix-icon-box">📞</span>
                      <div className="mobile-compound-input" style={{ display: 'flex', width: '100%', gap: '4px' }}>
                        <select className="mobile-code-select" style={{ width: '60px', height: '38px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 600, fontSize: '0.82rem', padding: '0 4px' }} tabIndex="-1">
                          <option value="+91">+91</option>
                        </select>
                        <input
                          type="tel"
                          maxLength={10}
                          className="field-input mobile-number-input"
                          style={{ flex: 1 }}
                          placeholder={getFieldPlaceholder('patient.mobile', '10-digit mobile number')}
                          value={patientData.mobile}
                          readOnly={isFieldReadOnly('patient.mobile')}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            setPatientData({ ...patientData, mobile: val });
                          }}
                          onBlur={checkDuplicate}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Email Address */}
                {isFieldVisible('patient.email') && (
                  <div className="grid-field span-2">
                    <label className="field-label">
                      {getFieldLabel('patient.email', 'Email Address')}
                      {isFieldRequired('patient.email') && <span className="req">*</span>}
                    </label>
                    <div className="field-control-with-icon">
                      <span className="field-prefix-icon-box">✉️</span>
                      <input
                        type="email"
                        className="field-input"
                        placeholder={getFieldPlaceholder('patient.email', 'patient@example.com')}
                        value={patientData.email}
                        readOnly={isFieldReadOnly('patient.email')}
                        onChange={(e) => setPatientData({ ...patientData, email: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                {/* ── ROW 2: Date of Birth (2), Age Y/M/D (3), Panel (4), Doctor (3) ── */}
                {/* Date of Birth (Direct Input, Bidirectional) */}
                {isFieldVisible('patient.dateOfBirth') && (
                  <div className="grid-field span-2">
                    <label className="field-label">
                      {getFieldLabel('patient.dateOfBirth', 'Date of Birth')}
                      {isFieldRequired('patient.dateOfBirth') && !patientData.ageYear && <span className="req">*</span>}
                    </label>
                    <div className="field-control-with-icon">
                      <span className="field-prefix-icon-box">📅</span>
                      <input
                        type="date"
                        className="field-input"
                        value={patientData.dob}
                        max={new Date().toISOString().split('T')[0]}
                        readOnly={isFieldReadOnly('patient.dateOfBirth')}
                        onFocus={() => setDobMode('dob')}
                        onChange={(e) => {
                          setDobMode('dob');
                          handleDobChange(e.target.value);
                        }}
                        title="Enter date of birth (auto-calculates age)"
                      />
                    </div>
                  </div>
                )}

                {/* Age (Years / Months / Days) (Direct Input, Bidirectional) */}
                {isFieldVisible('patient.age') && (
                  <div className={`grid-field span-3 ${ageValidationError ? 'has-field-error' : ''}`}>
                    <label className="field-label">
                      {getFieldLabel('patient.age', 'Age (Years)')}
                      {isFieldRequired('patient.age', true) && !patientData.dob && <span className="req">*</span>}
                    </label>
                    <div className="field-control-with-icon">
                      <span className="field-prefix-icon-box">🎂</span>
                      <div className="age-triplet-group" style={{ flex: 1 }}>
                        <input
                          type="number"
                          min="0"
                          max="120"
                          step="1"
                          className={`field-input age-part ${ageValidationError ? 'input-error-state' : ''}`}
                          placeholder="Years"
                          value={patientData.ageYear !== undefined && patientData.ageYear !== null ? patientData.ageYear : ''}
                          readOnly={isFieldReadOnly('patient.age')}
                          onFocus={() => setDobMode('age')}
                          onChange={(e) => {
                            setDobMode('age');
                            handleAgeChange(e.target.value, patientData.ageMonth, patientData.ageDays);
                          }}
                          title="Age in whole years (auto-calculates DOB)"
                        />
                        <input
                          type="number"
                          min="0"
                          max="11"
                          step="1"
                          className="field-input age-part"
                          placeholder="Mo"
                          value={patientData.ageMonth !== undefined && patientData.ageMonth !== null ? patientData.ageMonth : ''}
                          readOnly={isFieldReadOnly('patient.age')}
                          onFocus={() => setDobMode('age')}
                          onChange={(e) => {
                            setDobMode('age');
                            handleAgeChange(patientData.ageYear, e.target.value, patientData.ageDays);
                          }}
                          title="Months (0-11)"
                        />
                        <input
                          type="number"
                          min="0"
                          max="30"
                          step="1"
                          className="field-input age-part"
                          placeholder="Days"
                          value={patientData.ageDays !== undefined && patientData.ageDays !== null ? patientData.ageDays : ''}
                          readOnly={isFieldReadOnly('patient.age')}
                          onFocus={() => setDobMode('age')}
                          onChange={(e) => {
                            setDobMode('age');
                            handleAgeChange(patientData.ageYear, patientData.ageMonth, e.target.value);
                          }}
                          title="Days (0-30)"
                        />
                      </div>
                    </div>
                    {ageValidationError && (
                      <div className="field-inline-error-msg" role="alert">
                        <span className="field-error-icon">⚠️</span>
                        <span>{ageValidationError}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Unified Billing Panel / Client Sponsor Selector */}
                {isFieldVisible('registration.panel') && (
                  <div className="grid-field span-4">
                    <label className="field-label">
                      {getFieldLabel('registration.panel', 'Billing Panel / Client Sponsor')}
                      {isFieldRequired('registration.panel', true) && <span className="req">*</span>}
                    </label>
                    <div className="field-control-with-icon">
                      <span className="field-prefix-icon-box">{partyId ? '🏢' : '🗂️'}</span>
                      <select
                        className="field-select text-bold"
                        value={partyId ? `PARTY_${partyId}` : `PANEL_${panelId || 1}`}
                        disabled={isFieldReadOnly('registration.panel')}
                        onChange={(e) => handleBillingSponsorChange(e.target.value)}
                      >
                        <optgroup label="👤 Retail & Walk-In Panels (B2C)">
                          {lookups.panels.map((p) => (
                            <option key={`PANEL_${p.panelId}`} value={`PANEL_${p.panelId}`}>
                              {p.panelName} ({p.panelCode})
                            </option>
                          ))}
                        </optgroup>
                        {eligibleParties.length > 0 && (
                          <optgroup label="🏢 B2B Institutional Clients / Corporate Parties">
                            {eligibleParties.map((p) => (
                              <option key={`PARTY_${p.partyID}`} value={`PARTY_${p.partyID}`}>
                                {p.partyName} ({p.partyCode}){p.defaultPanelName ? ` — [${p.defaultPanelName}]` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                    {partyCreditStatus && (
                      <div style={{ fontSize: '11px', marginTop: '4px', color: partyCreditStatus.isActive ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                        Status: {partyCreditStatus.lifecycleStatus} &bull; Credit Limit: ₹{Number(partyCreditStatus.creditLimit || 0).toLocaleString()}
                        {panelId && (
                          <span style={{ color: '#64748b', marginLeft: '6px' }}>
                            (Tariff: {lookups.panels.find((p) => p.panelId === panelId)?.panelName || 'Standard'})
                          </span>
                        )}
                      </div>
                    )}
                    {partyGate && (
                      <div
                        role="alert"
                        style={{
                          fontSize: '12px', marginTop: '6px', padding: '6px 10px', borderRadius: '6px', fontWeight: 600,
                          background: partyGate.block ? '#fef2f2' : '#fffbeb', color: partyGate.block ? '#b91c1c' : '#b45309',
                        }}
                      >
                        {partyGate.block ? '🚫 ' : '⚠️ '}{partyGate.message}
                        {partyGate.block ? " This client's terms may stop the bill from being saved." : ''}
                      </div>
                    )}
                  </div>
                )}

                {partyId && (
                  <div className="grid-field span-3">
                    <label className="field-label">External Order / Accession #</label>
                    <div className="field-control-with-icon">
                      <span className="field-prefix-icon-box">🏷️</span>
                      <input
                        type="text"
                        className="field-input"
                        placeholder="e.g. EXT-2026-9901"
                        value={clientOrderNumber}
                        onChange={(e) => setClientOrderNumber(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* Referred Doctor */}
                {isFieldVisible('referral.referringDoctor') && (
                  <div className="grid-field span-3">
                    <label className="field-label">
                      {getFieldLabel('referral.referringDoctor', 'Referring Clinician')}
                      {isFieldRequired('referral.referringDoctor', true) && <span className="req">*</span>}
                    </label>
                    <div className="field-control-with-icon">
                      <span className="field-prefix-icon-box">👨‍⚕️</span>
                      <select
                        className="field-select text-bold"
                        value={doctorId}
                        disabled={isFieldReadOnly('referral.referringDoctor')}
                        onChange={(e) => handleDoctorChange(e.target.value)}
                      >
                        {lookups.doctors.map((d) => (
                          <option key={d.doctorId} value={d.doctorId}>
                            {d.doctorName} {d.specialization ? `(${d.specialization})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* ── ROW 3: Referred by Other (3), Cascading Location (9) ── */}
                {/* Referred by Other (Clinic / Center / External Partner) */}
                {isFieldVisible('referral.source') && (
                  <div className="grid-field span-3">
                    <label className="field-label">
                      {getFieldLabel('referral.source', 'Referral Source')}
                    </label>
                    <div className="field-control-with-icon">
                      <span className="field-prefix-icon-box">🔄</span>
                      <select
                        className="field-select"
                        value={referralSourceId}
                        disabled={isFieldReadOnly('referral.source')}
                        onChange={(e) => handleReferralSourceChange(e.target.value)}
                      >
                        <option value="">-- None / Direct --</option>
                        {referralSources.map((s) => {
                          const sId = s.sourceID || s.sourceId || s.id;
                          const sName = s.sourceName || s.name || `Source #${sId}`;
                          const sType = s.sourceType || 'CLINIC';
                          return (
                            <option key={sId} value={sId}>
                              {sName} ({sType})
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                )}

                {/* Cascading Location Hierarchy (Country, State, City, Address, Pincode) */}
                <CascadingLocationSelector
                  countryId={patientData.countryId || 1}
                  stateId={patientData.stateId}
                  cityId={patientData.cityId}
                  pinCode={patientData.pincode}
                  address={patientData.address}
                  countryName={patientData.country || 'India'}
                  stateName={patientData.state}
                  cityName={patientData.city}
                  onChange={(loc) => {
                    setPatientData((prev) => ({
                      ...prev,
                      countryId: loc.countryId,
                      country: loc.countryName,
                      stateId: loc.stateId,
                      state: loc.stateName,
                      cityId: loc.cityId,
                      city: loc.cityName,
                      pincode: loc.pinCode,
                      address: loc.address,
                    }));
                  }}
                  layout="unified-workbench"
                  addressSpan={6}
                  addressAside={(
                    <>
                      <label className="loc-label" htmlFor="reg-collection-boy">
                        Collection Boy {isFieldRequired('referral.phlebotomist') && <span className="loc-req">*</span>}
                      </label>
                      <div className="field-control-with-icon">
                        <span className="field-prefix-icon-box" aria-hidden="true">🛵</span>
                        <div className="loc-select-wrap" style={{ flex: 1, width: '100%' }}>
                          <select
                            id="reg-collection-boy"
                            className="loc-select"
                            value={collectionBoys.find((b) => b.boyName === phlebotomistName)?.collectionBoyId || ''}
                            onChange={(e) => setPhlebotomistName(collectionBoys.find((b) => String(b.collectionBoyId) === e.target.value)?.boyName || '')}
                          >
                            <option value="">
                              {collectionBoysFailed ? 'Could not load the list' : collectionBoys.length === 0 ? 'No collection boy added' : '-- None / Walk-in --'}
                            </option>
                            {collectionBoys.map((b) => (
                              <option key={b.collectionBoyId} value={b.collectionBoyId}>{b.boyName}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                  showAddress={isFieldVisible('patient.addressLine1')}
                  showPinCode={isFieldVisible('patient.pincode')}
                  showCity={isFieldVisible('patient.city')}
                  showState={isFieldVisible('patient.state')}
                  required={{
                    address: isFieldRequired('patient.addressLine1'),
                    city: isFieldRequired('patient.city'),
                    state: isFieldRequired('patient.state'),
                    pinCode: isFieldRequired('patient.pincode'),
                  }}
                />

                {/* Secondary Collapsible Drawers Action Toolbar */}
                <div className="span-12 reg-drawers-toolbar">
                  <button
                    type="button"
                    className={`drawer-chip-btn ${guardianExpanded ? 'active' : ''}`}
                    onClick={() => setGuardianExpanded((prev) => !prev)}
                  >
                    👤 Guardian / Kin {patientData.guardianName ? `(${patientData.guardianName})` : '+ Add'}
                  </button>
                  <button
                    type="button"
                    className={`drawer-chip-btn ${identificationExpanded ? 'active' : ''}`}
                    onClick={() => setIdentificationExpanded((prev) => !prev)}
                  >
                    🪪 Govt ID & ABHA {patientData.governmentIdNumber ? `(${patientData.governmentIdType})` : '+ Add'}
                  </button>
                  <button
                    type="button"
                    className={`drawer-chip-btn ${clinicalNotesExpanded ? 'active' : ''}`}
                    onClick={() => setClinicalNotesExpanded((prev) => !prev)}
                  >
                    📋 Clinical Notes {patientData.clinicalHistory ? '✓ Added' : '+ Add'}
                  </button>
                  <button
                    type="button"
                    className={`drawer-chip-btn ${documentsExpanded ? 'active' : ''}`}
                    onClick={() => setDocumentsExpanded((prev) => !prev)}
                  >
                    📎 Documents {patientDocuments.length > 0 ? `(${patientDocuments.length})` : '+ Attach'}
                  </button>
                  <button
                    type="button"
                    className={`drawer-chip-btn ${showAdditionalDetails ? 'active' : ''}`}
                    onClick={() => setShowAdditionalDetails((prev) => !prev)}
                  >
                    ⚙️ Additional Details (Visit, Vital, RRF!)
                  </button>
                </div>

                {/* DRAWER: Guardian & Emergency Contact */}
                {guardianExpanded && (
                  <div className="span-12 drawer-content-panel animate-fade-in">
                    <div className="drawer-panel-header">
                      <strong>🛡️ Guardian & Emergency Contact Information</strong>
                      <button type="button" className="drawer-close-btn" onClick={() => setGuardianExpanded(false)}>✕</button>
                    </div>
                    <div className="reg-dense-grid-12" style={{ marginTop: '8px' }}>
                      {isFieldVisible('patient.guardianName') && (
                        <div className="grid-field span-4">
                          <label className="field-label">
                            {getFieldLabel('patient.guardianName', 'Guardian Name')}
                            {isFieldRequired('patient.guardianName') && <span className="req">*</span>}
                          </label>
                          <input
                            type="text"
                            className="field-input"
                            placeholder="Full Name of Guardian"
                            value={patientData.guardianName}
                            readOnly={isFieldReadOnly('patient.guardianName')}
                            onChange={(e) => setPatientData({ ...patientData, guardianName: e.target.value })}
                          />
                        </div>
                      )}
                      {isFieldVisible('patient.guardianRelation') && (
                        <div className="grid-field span-4">
                          <label className="field-label">Relationship to Patient</label>
                          <select
                            className="field-select"
                            value={patientData.guardianRelation}
                            disabled={isFieldReadOnly('patient.guardianRelation')}
                            onChange={(e) => setPatientData({ ...patientData, guardianRelation: e.target.value })}
                          >
                            <option value="Father">Father</option>
                            <option value="Mother">Mother</option>
                            <option value="Spouse">Spouse</option>
                            <option value="Son / Daughter">Son / Daughter</option>
                            <option value="Legal Guardian">Legal Guardian</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      )}
                      {isFieldVisible('patient.guardianMobile') && (
                        <div className="grid-field span-4">
                          <label className="field-label">Guardian Mobile</label>
                          <input
                            type="tel"
                            maxLength={10}
                            className="field-input"
                            placeholder="10-digit mobile"
                            value={patientData.guardianMobile}
                            readOnly={isFieldReadOnly('patient.guardianMobile')}
                            onChange={(e) => setPatientData({ ...patientData, guardianMobile: e.target.value.replace(/\D/g, '') })}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* DRAWER: Government ID & ABHA */}
                {identificationExpanded && (
                  <div className="span-12 drawer-content-panel animate-fade-in">
                    <div className="drawer-panel-header">
                      <strong>🪪 Government Identification & Ayushman Bharat (ABHA)</strong>
                      <button type="button" className="drawer-close-btn" onClick={() => setIdentificationExpanded(false)}>✕</button>
                    </div>
                    <div className="reg-dense-grid-12" style={{ marginTop: '8px' }}>
                      {isFieldVisible('patient.governmentIdType') && (
                        <div className="grid-field span-4">
                          <label className="field-label">Document Identification Type</label>
                          <select
                            className="field-select"
                            value={patientData.governmentIdType}
                            disabled={isFieldReadOnly('patient.governmentIdType')}
                            onChange={(e) => setPatientData({ ...patientData, governmentIdType: e.target.value })}
                          >
                            <option value="Aadhaar Card">Aadhaar Card</option>
                            <option value="Passport">Passport</option>
                            <option value="Driving Licence">Driving Licence</option>
                            <option value="Voter ID">Voter ID</option>
                            <option value="PAN Card">PAN Card</option>
                            <option value="Corporate / Employee ID">Corporate / Employee ID</option>
                            <option value="Other">Other Government ID</option>
                          </select>
                        </div>
                      )}
                      {isFieldVisible('patient.governmentIdNumber') && (
                        <div className="grid-field span-4">
                          <label className="field-label">Document Identification Number</label>
                          <input
                            type="text"
                            className="field-input"
                            placeholder="Enter ID number"
                            value={patientData.governmentIdNumber}
                            readOnly={isFieldReadOnly('patient.governmentIdNumber')}
                            onChange={(e) => setPatientData({ ...patientData, governmentIdNumber: e.target.value })}
                          />
                        </div>
                      )}
                      {isFieldVisible('patient.abha') && (
                        <div className="grid-field span-4">
                          <label className="field-label">ABHA ID (Ayushman Bharat)</label>
                          <input
                            type="text"
                            className="field-input"
                            placeholder="e.g. 14-digit ABHA ID"
                            value={patientData.abhaId}
                            readOnly={isFieldReadOnly('patient.abha')}
                            onChange={(e) => setPatientData({ ...patientData, abhaId: e.target.value })}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* DRAWER: Clinical History & Notes */}
                {clinicalNotesExpanded && (
                  <div className="span-12 drawer-content-panel animate-fade-in">
                    <div className="drawer-panel-header">
                      <strong>📝 Clinical History, Presenting Symptoms & Diagnostic Notes</strong>
                      <button type="button" className="drawer-close-btn" onClick={() => setClinicalNotesExpanded(false)}>✕</button>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <textarea
                        rows={2}
                        className="field-textarea"
                        style={{ width: '100%', fontSize: '0.8rem', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                        placeholder="Enter clinical indication, symptoms, fasting status, provisional diagnosis, or physician instructions..."
                        value={patientData.clinicalHistory}
                        readOnly={isFieldReadOnly('clinical.history')}
                        onChange={(e) => setPatientData({ ...patientData, clinicalHistory: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                {/* DRAWER: Documents & Attachments */}
                {documentsExpanded && (
                  <div className="span-12 drawer-content-panel animate-fade-in">
                    <div className="drawer-panel-header">
                      <strong>📎 Attached Documents, Prescriptions & Reports</strong>
                      <button type="button" className="drawer-close-btn" onClick={() => setDocumentsExpanded(false)}>✕</button>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <div className="reg-doc-compact-panel">
                        <div className="reg-doc-upload-strip">
                          <select
                            value={selectedDocType}
                            onChange={(e) => setSelectedDocType(e.target.value)}
                          >
                            {lookups.documentTypes.map((dt) => (
                              <option key={dt.code} value={dt.value}>{dt.value}</option>
                            ))}
                          </select>
                          <input
                            type="file"
                            ref={docFileInputRef}
                            style={{ display: 'none' }}
                            accept="application/pdf,image/jpeg,image/png,image/webp"
                            onChange={handleDocumentUpload}
                          />
                          <button
                            type="button"
                            className="btn-upload-compact"
                            disabled={docUploading}
                            onClick={() => docFileInputRef.current?.click()}
                          >
                            {docUploading ? '⏳ Uploading...' : '📎 Choose File & Attach'}
                          </button>
                        </div>
                        {patientDocuments.length > 0 ? (
                          <div className="reg-doc-pill-list" style={{ marginTop: '6px' }}>
                            {patientDocuments.map((doc) => (
                              <div key={doc.documentId} className="reg-doc-pill-item">
                                <div className="reg-doc-pill-left">
                                  <span>{doc.mimeType === 'application/pdf' ? '📄' : '🖼️'}</span>
                                  <strong title={doc.documentName}>{doc.documentName}</strong>
                                  <span style={{ fontSize: '0.66rem', color: '#64748b' }}>({(doc.fileSize / 1024).toFixed(0)} KB)</span>
                                </div>
                                <div className="reg-doc-pill-actions">
                                  <button type="button" onClick={() => setPreviewDoc(doc)} title="Preview">👁️</button>
                                  <a href={patientDocumentService.getDocumentDownloadUrl(doc.documentId)} download title="Download">⬇️</a>
                                  <button type="button" onClick={() => handleDeleteDocument(doc.documentId)} title="Remove" style={{ color: '#ef4444' }}>✕</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="reg-doc-empty-hint" style={{ marginTop: '6px' }}>
                            No files attached. Select category and click Choose File & Attach.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* DRAWER: Additional Details & Vitals */}
                {showAdditionalDetails && (
                  <div className="span-12 drawer-content-panel animate-fade-in">
                    <div className="drawer-panel-header">
                      <strong>⚙️ Additional Clinical & Encounter Details</strong>
                      <button type="button" className="drawer-close-btn" onClick={() => setShowAdditionalDetails(false)}>✕</button>
                    </div>
                    <div className="reg-dense-grid-12" style={{ marginTop: '8px' }}>
                      {isFieldVisible('patient.maritalStatus') && (
                        <div className="grid-field span-2">
                          <label className="field-label">Marital Status</label>
                          <select className="field-select" value={patientData.maritalStatus} onChange={(e) => setPatientData({ ...patientData, maritalStatus: e.target.value })}>
                            {lookups.maritalStatuses.map((m) => (
                              <option key={m.code} value={m.value}>{m.value}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {isFieldVisible('registration.scheduledCollection') && (
                        <div className="grid-field span-4">
                          <label className="field-label">Scheduled Collection</label>
                          <div className="date-time-combo" style={{ display: 'flex', gap: '4px' }}>
                            <input type="date" className="field-input date-part" value={collectionDate} onChange={(e) => setCollectionDate(e.target.value)} />
                            <input type="time" className="field-input time-part" value={collectionTime} onChange={(e) => setCollectionTime(e.target.value)} />
                          </div>
                        </div>
                      )}
                      {isFieldVisible('registration.visitType') && (
                        <div className="grid-field span-2">
                          <label className="field-label">Visit Type</label>
                          <select className="field-select" value={visitType} onChange={(e) => setVisitType(e.target.value)}>
                            {lookups.visitTypes.map((vt) => (
                              <option key={vt.code} value={vt.value}>{vt.value}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {isFieldVisible('registration.patientType') && (
                        <div className="grid-field span-2">
                          <label className="field-label">Classification</label>
                          <select className="field-select" value={patientType} onChange={(e) => setPatientType(e.target.value)}>
                            {lookups.patientTypes.map((pt) => (
                              <option key={pt.code} value={pt.value}>{pt.value}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {isFieldVisible('patient.bloodGroup') && (
                        <div className="grid-field span-2">
                          <label className="field-label">Blood Group</label>
                          <select className="field-select" value={patientData.bloodGroup} onChange={(e) => setPatientData({ ...patientData, bloodGroup: e.target.value })}>
                            <option value="">-- Select --</option>
                            {lookups.bloodGroups.map((b) => (
                              <option key={b.code} value={b.value}>{b.value}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {isFieldVisible('patient.weight') && (
                        <div className="grid-field span-2">
                          <label className="field-label">Weight (kg)</label>
                          <input type="number" step="0.1" className="field-input" placeholder="e.g. 68.5" value={patientData.weight} onChange={(e) => setPatientData({ ...patientData, weight: e.target.value })} />
                        </div>
                      )}
                      {isFieldVisible('patient.height') && (
                        <div className="grid-field span-2">
                          <label className="field-label">Height (cm)</label>
                          <input type="number" step="0.5" className="field-input" placeholder="e.g. 172" value={patientData.height} onChange={(e) => setPatientData({ ...patientData, height: e.target.value })} />
                        </div>
                      )}
                      {isFieldVisible('patient.lmp') && patientData.gender === 'Female' && (
                        <div className="grid-field span-2">
                          <label className="field-label">LMP Date</label>
                          <input type="date" className="field-input" value={patientData.lmp} onChange={(e) => setPatientData({ ...patientData, lmp: e.target.value })} />
                        </div>
                      )}
                      {isFieldVisible('registration.srfId') && (
                        <div className="grid-field span-3">
                          <label className="field-label">SRF / ICMR ID</label>
                          <input type="text" className="field-input" placeholder="Govt SRF ID" value={srfId} onChange={(e) => setSrfId(e.target.value)} />
                        </div>
                      )}
                      {isFieldVisible('referral.referringLab') && (
                        <div className="grid-field span-3">
                          <label className="field-label">Referring Lab Name</label>
                          <input type="text" className="field-input" placeholder="External lab name" value={referringLab} onChange={(e) => setReferringLab(e.target.value)} />
                        </div>
                      )}
                      {isFieldVisible('referral.pro') && (
                        <div className="grid-field span-3">
                          <label className="field-label">Assigned PRO</label>
                          <input type="text" className="field-input" placeholder="PRO Name" value={proName} onChange={(e) => setProName(e.target.value)} />
                        </div>
                      )}
                      {isFieldVisible('patient.nationality') && (
                        <div className="grid-field span-3">
                          <label className="field-label">Nationality</label>
                          <select className="field-select" value={patientData.nationality} onChange={(e) => setPatientData({ ...patientData, nationality: e.target.value })}>
                            {lookups.nationalities.map((n) => (
                              <option key={n.code} value={n.value}>{n.value}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Two-Column Orders & Billing Layout */}
          <div className="orders-billing-workbench-grid">
            {/* Left Column: Investigation Catalog & Cart Table */}
            <div className="orders-left-column">
              {/* Investigation Search & Quick Category Filters */}
              <div className="futuristic-card">
                <div className="card-header-bar catalog-header-bar">
                  <div className="card-title-group">
                    <span className="card-icon card-icon-green">🧪</span>
                    <div>
                      <h3 className="card-title">Diagnostic Catalog & Service Order Search</h3>
                      <span className="catalog-subtitle">Search and add tests, profiles or packages to this order</span>
                    </div>
                  </div>
                  <div className="catalog-stats-chips">
                    <span className="cat-stat-chip total">Total: {invStats.total}</span>
                    <span className="cat-stat-chip path">Tests: {invStats.tests}</span>
                    <span className="cat-stat-chip rad">Profiles: {invStats.profiles}</span>
                    <span className="cat-stat-chip pkg">Packages: {invStats.packages}</span>
                    {invStats.urgent > 0 && (
                      <span className="cat-stat-chip urgent">Urgent STAT: {invStats.urgent}</span>
                    )}
                  </div>
                </div>

                <div className="card-body-content">
                  {/* Command-style test search */}
                  <div className="test-search-wrap">
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="test-search-input"
                      placeholder="Search tests, profiles, or health packages (e.g. CBC, Lipid, Full Body, VP001, Thyroid)..."
                      value={catalogSearch}
                      onChange={(e) => setCatalogSearch(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                    />
                    {catalogLoading && <span className="test-search-loading">Searching...</span>}
                    {catalogSearch && (
                      <button
                        type="button"
                        className="test-search-clear"
                        onClick={() => {
                          setCatalogSearch('');
                          setInvestigationsCatalog([]);
                          setHighlightedIndex(-1);
                        }}
                      >
                        ✕
                      </button>
                    )}

                    {/* Dynamic Dropdown Results */}
                    {investigationsCatalog.length > 0 && (
                      <div className="test-search-dropdown" ref={dropdownListRef}>
                        <div className="test-dropdown-header">
                          <span>Available catalog items ({investigationsCatalog.length} results):</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <kbd style={{ fontSize: '10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '3px', padding: '1px 5px', color: '#4b5563' }}>↑</kbd>
                            <kbd style={{ fontSize: '10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '3px', padding: '1px 5px', color: '#4b5563' }}>↓</kbd> Navigate
                            <span style={{ margin: '0 2px' }}>•</span>
                            <kbd style={{ fontSize: '10px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '3px', padding: '1px 5px', color: '#047857', fontWeight: 700 }}>Enter ↵</kbd> Select
                          </span>
                        </div>
                        {investigationsCatalog.map((inv, idx) => {
                          const isHighlighted = highlightedIndex === idx;
                          return (
                            <div
                              key={`${inv.itemType}-${inv.itemId || inv.code}`}
                              data-index={idx}
                              className={`test-result-item ${isHighlighted ? 'highlighted active-item' : ''} ${inv.isOutsource ? 'test-result-outsource' : ''}`}
                              onMouseEnter={() => setHighlightedIndex(idx)}
                              onClick={() => handleAddTest(inv)}
                            >
                              <div className="test-result-left">
                                <span className={`item-type-badge ${(inv.itemType || 'test').toLowerCase()}`}>
                                  {inv.itemType === 'PACKAGE' ? '📦 PACKAGE' : inv.itemType === 'PROFILE' ? '📋 PROFILE' : '🧪 TEST'}
                                </span>
                                {inv.isOutsource && (
                                  <span className="badge-outsource-dropdown" title={inv.referenceLabName ? `Partner Lab: ${inv.referenceLabName}` : 'Outsourced Investigation'}>
                                    🌐 OUTSOURCE{inv.referenceLabName ? ` • ${inv.referenceLabName}` : ''}
                                  </span>
                                )}
                                <span className="test-result-code">[{inv.code}]</span>
                                <strong className="test-result-name">{inv.name}</strong>
                                <span className="test-result-dept">{inv.category}</span>
                                {inv.sample && (
                                  <span className="test-result-sample">
                                    {inv.itemType === 'TEST' ? `🧪 ${inv.sample}` : `📋 ${inv.sample}`}
                                  </span>
                                )}
                              </div>
                              <div className="test-result-right">
                                {inv.baseRate > 0 && (
                                  <span className="test-result-rate">₹ {parseFloat(inv.baseRate).toFixed(2)}</span>
                                )}
                                <button
                                  type="button"
                                  className={`btn-add-test ${isHighlighted ? 'btn-add-test-highlighted' : ''}`}
                                >
                                  {isHighlighted ? '+ Add ↵' : '+ Add'}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Selected Investigations Table */}
                  {displayedTests.length > 0 ? (
                    <div className="futuristic-table-wrapper">
                      <table className="futuristic-data-table">
                        <thead>
                          <tr>
                            <th style={{ width: '28px' }}>#</th>
                            <th style={{ width: '72px' }}>Type</th>
                            <th style={{ width: '60px' }}>Code</th>
                            <th style={{ minWidth: '120px' }}>Investigation / Service Name</th>
                            <th style={{ minWidth: '90px' }}>Department</th>
                            <th style={{ minWidth: '85px' }}>Specimen</th>
                            <th style={{ width: '80px', textAlign: 'center' }}>Priority</th>
                            <th style={{ width: '80px', textAlign: 'right' }}>Rate (₹)</th>
                            <th style={{ width: '80px', textAlign: 'right' }}>Net (₹)</th>
                            <th style={{ width: '40px', textAlign: 'center' }}>Act</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedTests.map((t, idx) => (
                            <tr key={`${t.itemType || 'test'}-${t.itemId}`} className={`${t.isUrgent ? 'row-stat-urgent' : ''} ${t.isOutsource ? 'row-outsource-test' : ''}`}>
                              <td className="cell-muted">{idx + 1}</td>
                              <td>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  <span className={`item-type-badge ${(t.itemType || 'test').toLowerCase()}`}>
                                    {t.itemType === 'PACKAGE' ? '📦 PKG' : t.itemType === 'PROFILE' ? '📋 PROF' : '🧪 TEST'}
                                  </span>
                                  {t.isOutsource && (
                                    <span className="badge-outsource-mini" title={t.referenceLabName || 'Outsource Lab'}>
                                      🌐 OUTSOURCE
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className="cell-code-badge">{t.itemCode}</span>
                              </td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                  <strong className="cell-test-name">{t.itemName}</strong>
                                  {t.isOutsource && (
                                    <span className="badge-outsource-table" title={t.referenceLabName ? `Partner Lab: ${t.referenceLabName}` : 'Outsourced to Reference Laboratory'}>
                                      🌐 OUTSOURCE{t.referenceLabName ? ` • ${t.referenceLabName}` : ''}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className="cell-dept-tag">{t.subCategoryName || 'Diagnostics'}</span>
                              </td>
                              <td>
                                <span className="cell-sample-tag">
                                  {t.sampleTypeName ? (t.sampleTypeName.startsWith('🧪') || t.sampleTypeName.startsWith('📋') || t.sampleTypeName.startsWith('Multi') ? t.sampleTypeName : `🧪 ${t.sampleTypeName}`) : '—'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <button
                                  type="button"
                                  className={`stat-toggle-pill ${t.isUrgent ? 'active-urgent' : 'routine'}`}
                                  onClick={() => handleToggleUrgent(t.itemId)}
                                  title="Toggle Routine vs Urgent STAT priority"
                                >
                                  {t.isUrgent ? '⚡ STAT URGENT' : 'Routine'}
                                </button>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                ₹ {t.rate.toFixed(2)}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669' }}>
                                ₹ {t.rate.toFixed(2)}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <button
                                  type="button"
                                  className="btn-table-remove"
                                  title="Remove test from order"
                                  onClick={() => handleRemoveTest(t.itemId)}
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="futuristic-empty-state">
                      <div className="empty-state-doc-icon" style={{ marginBottom: '14px' }}>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                          <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 2V8H20" stroke="#bfdbfe" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="11.5" cy="14.5" r="3.5" fill="#ffffff" stroke="#3b82f6" strokeWidth="2"/>
                          <path d="M14.5 17.5L18 21" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div className="empty-title">
                        {selectedTests.length === 0 ? 'No Investigations or Packages Ordered' : 'No items matching filter'}
                      </div>
                      <div className="empty-desc">
                        {selectedTests.length === 0
                          ? 'Search test code or name above (e.g., CBC, Lipid Profile, Full Body Package, VP001) to add tests to this order.'
                          : 'Change the filter above to view all selected items.'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Sticky Billing Summary & Settlement Panel */}
            <div className="orders-right-column">
              <div className="sticky-billing-card cashier-billing-card">
                {/* Cashier Station Card Header */}
                <div className="card-header-bar cashier-header-bar">
                  <div className="card-title-group">
                    <span className="card-icon card-icon-purple">🧮</span>
                    <div>
                      <h3 className="card-title">Order Billing & Settlement</h3>
                      <span className="cashier-subtitle">Cashier-First Workstation</span>
                    </div>
                  </div>
                  <div className="cashier-status-pill-wrap">
                    {partyId ? (
                      <span className="cashier-status-badge partial">🏢 On client credit</span>
                    ) : isOverpaid ? (
                      <span className="cashier-status-badge error" style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }}>
                        ⚠️ Overpaid ₹{overpaymentAmount.toFixed(2)}
                      </span>
                    ) : netAmount === 0 ? (
                      <span className="cashier-status-badge free">Free / No Charge</span>
                    ) : totalPaid >= netAmount ? (
                      <span className="cashier-status-badge complete">✓ Payment Complete</span>
                    ) : totalPaid > 0 ? (
                      <span className="cashier-status-badge partial">⏳ Partial Paid</span>
                    ) : (
                      <span className="cashier-status-badge pending">🟡 Payment Pending</span>
                    )}
                  </div>
                </div>

                <div className="card-body-content cashier-card-body">
                  {/* Visual Payment Summary Banner (Prominent KPI Cards) */}
                  <div className="cashier-summary-banner">
                    <div className="cashier-metric-card gross" style={{ borderTop: '4px solid #64748b' }}>
                      <span className="cashier-metric-label">GROSS AMOUNT</span>
                      <strong className="cashier-metric-value" style={{ color: '#475569' }}>₹ {grossAmount.toFixed(2)}</strong>
                      <span className="cashier-metric-sub">
                        {selectedTests.length} Item{selectedTests.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {discountAmount > 0 && (
                      <div className="cashier-metric-card discount" style={{ borderTop: '4px solid #eab308' }}>
                        <span className="cashier-metric-label">DISCOUNT</span>
                        <strong className="cashier-metric-value" style={{ color: '#ca8a04' }}>-₹ {discountAmount.toFixed(2)}</strong>
                        <span className="cashier-metric-sub">
                          {discountType === 'Percentage' ? `${discountOnTotal}%` : 'Fixed'}
                        </span>
                      </div>
                    )}

                    {careCoinsDiscountEstimate > 0 && (
                      <div className="cashier-metric-card carecoins" style={{ borderTop: '4px solid #0891b2' }}>
                        <span className="cashier-metric-label">CARECOINS</span>
                        <strong className="cashier-metric-value" style={{ color: '#0e7490' }}>-₹ {careCoinsDiscountEstimate.toFixed(2)}</strong>
                        <span className="cashier-metric-sub">
                          {careCoinsPreview?.pointsRequested || parseInt(careCoinsPointsToRedeem, 10) || 0} pts redeemed
                        </span>
                      </div>
                    )}

                    {collectionCharge.amount > 0 && (
                      <div className="cashier-metric-card collection-charge" style={{ borderTop: '4px solid #7c3aed' }}>
                        <span className="cashier-metric-label">COLLECTION CHARGE</span>
                        <strong className="cashier-metric-value" style={{ color: '#6d28d9' }}>+₹ {collectionCharge.amount.toFixed(2)}</strong>
                        <span className="cashier-metric-sub">Billed separately from tests</span>
                      </div>
                    )}

                    <div className="cashier-metric-card payable">
                      <span className="cashier-metric-label">NET PAYABLE</span>
                      <strong className="cashier-metric-value">₹ {netAmount.toFixed(2)}</strong>
                      <span className="cashier-metric-sub">

                        Gross: ₹{grossAmount.toFixed(2)}{discountAmount > 0 ? ` • Disc: -₹${discountAmount.toFixed(2)}` : ''}{careCoinsDiscountEstimate > 0 ? ` • CareCoins: -₹${careCoinsDiscountEstimate.toFixed(2)}` : ''}
                      </span>
                    </div>

                    <div className="cashier-metric-card paid">
                      <span className="cashier-metric-label">TOTAL PAID</span>
                      <strong className="cashier-metric-value text-success">₹ {totalPaid.toFixed(2)}</strong>
                      <span className="cashier-metric-sub">
                        {paymentsList.length} Payment{paymentsList.length !== 1 ? 's' : ''} Applied
                      </span>
                    </div>

                    <div className={`cashier-metric-card due ${remainingBalance > 0 ? 'has-due' : 'settled'}`}>
                      <span className="cashier-metric-label">BALANCE DUE</span>
                      <strong className="cashier-metric-value text-due">₹ {remainingBalance.toFixed(2)}</strong>
                      <span className="cashier-metric-sub">
                        {partyId ? 'On client credit' : (remainingBalance > 0 ? 'Pending Collection' : '✓ Fully Settled')}
                      </span>
                    </div>
                  </div>

                  {/* Overpayment Alert Callout */}
                  {isOverpaid && (
                    <div style={{
                      background: '#fff1f2',
                      border: '1px solid #fecdd3',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      marginBottom: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      color: '#9f1239',
                      fontSize: '12px',
                      fontWeight: 600,
                      lineHeight: '1.4'
                    }}>
                      <span style={{ fontSize: '18px', flexShrink: 0 }}>⚠️</span>
                      <div>
                        <div><strong>Overpayment Alert:</strong> Total Recorded Payments (₹{totalPaid.toFixed(2)}) exceed Net Payable (₹{netAmount.toFixed(2)}) by ₹{overpaymentAmount.toFixed(2)}.</div>
                        <div style={{ fontWeight: 400, marginTop: '2px', color: '#be123c' }}>
                          Please reduce concession discount or delete excess payment entries from the Payment History table below before registering.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Financial Sequence: 3 Equal-Spaced Compact Parts */}
                  <div className="cashier-financial-sequence">
                    <div className="fin-single-line-bar">
                      {/* Part 1: Gross Tests Total */}
                      <div className="fin-bar-item fin-bar-gross">
                        <span className="fin-label-inline">Gross ({selectedTests.length}):</span>
                        <strong className="fin-val-inline">₹ {grossAmount.toFixed(2)}</strong>
                      </div>

                      <div className="fin-bar-separator" />

                      {/* Part 2: Concession Type Dropdown */}
                      <div className="fin-bar-item fin-bar-type">
                        <span className="fin-label-inline">Concession:</span>
                        <select
                          className="fin-disc-type-select-compact"
                          value={discountType}
                          onChange={(e) => {
                            setDiscountType(e.target.value);
                            setIsPayAmountManuallyEdited(false);
                          }}
                          title="Discount Type (Amount / Percentage)"
                        >
                          <option value="Amount">₹ Fixed</option>
                          <option value="Percentage">% Percentage</option>
                        </select>
                      </div>

                      <div className="fin-bar-separator" />

                      {/* Part 3: Discount Value Input */}
                      <div className="fin-bar-item fin-bar-amount">
                        <span className="fin-label-inline">{discountType === 'Percentage' ? 'Rate (%):' : 'Amount:'}</span>
                        <input
                          type="number"
                          min="0"
                          max={discountType === 'Percentage' ? 100 : grossAmount}
                          step={discountType === 'Percentage' ? '1' : 'any'}
                          className="fin-disc-input-compact"
                          value={discountOnTotal}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setDiscountOnTotal('');
                              return;
                            }
                            const num = parseFloat(val);
                            if (isNaN(num) || num < 0) {
                              setDiscountOnTotal(0);
                              return;
                            }
                            if (discountType === 'Percentage' && num > 100) {
                              setDiscountOnTotal(100);
                              return;
                            }
                            if (discountType === 'Amount' && num > grossAmount) {
                              setDiscountOnTotal(grossAmount);
                              return;
                            }
                            setDiscountOnTotal(num);
                          }}
                          placeholder="0"
                          title="Enter Discount Amount"
                        />
                      </div>
                    </div>

                    {discountAmount > 0 && (
                      <div className="fin-discount-reason-wrap">
                        <div className="fin-reason-inline-row">
                          <div className="fin-reason-select-part">
                            <span className="field-label-inline-reason req-star">Discount Reason:</span>
                            <select
                              className="field-input-compact-reason"
                              value={selectedReasonId}
                              onChange={(e) => {
                                const rId = e.target.value;
                                setSelectedReasonId(rId);
                                const rObj = discountReasons.find((r) => String(r.reasonId) === String(rId));
                                if (rObj) {
                                  setDiscountReason(rObj.reasonName);
                                } else if (rId === 'CUSTOM') {
                                  setDiscountReason('');
                                }
                              }}
                            >
                              <option value="">-- Select Reason --</option>
                              {discountReasons.map((r) => (
                                <option key={r.reasonId} value={r.reasonId}>
                                  {r.reasonName} {r.requiresApproval ? ' (Approval Req.)' : ''}
                                </option>
                              ))}
                              <option value="CUSTOM">Other / Custom</option>
                            </select>
                          </div>

                          <div className="fin-reason-status-part">
                            {isValidatingDiscount ? (
                              <span className="status-badge-inline validating">Checking...</span>
                            ) : approvalRequest.status === 'APPROVED' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span className="status-badge-inline approved" title={`Approved by ${approvalRequest.approvedByName}`}>
                                  ✓ Approved
                                </span>
                                {approvalRequest.counterOfferMessage && (
                                  <div style={{ fontSize: '0.8rem', color: '#b45309', background: '#fef3c7', padding: '6px 10px', borderRadius: '6px', border: '1px solid #fcd34d', marginTop: '4px' }}>
                                    {approvalRequest.counterOfferMessage}
                                  </div>
                                )}
                              </div>
                            ) : approvalRequest.status === 'PENDING' ? (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <span className="status-badge-inline pending">⏳ Pending</span>
                                <button
                                  type="button"
                                  className="btn-check-status-mini"
                                  onClick={handleCheckApprovalStatus}
                                  title="Check Status"
                                >
                                  🔄
                                </button>
                              </div>
                            ) : approvalRequest.status === 'REJECTED' ? (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <span className="status-badge-inline rejected" title={approvalRequest.rejectionReason}>
                                  ✕ Rejected
                                </span>
                                <button
                                  type="button"
                                  className="btn-retry-status-mini"
                                  onClick={() =>
                                    setApprovalRequest({
                                      status: 'IDLE',
                                      requestId: null,
                                      requestNo: '',
                                      approvedByName: '',
                                      rejectionReason: '',
                                      submitting: false
                                    })
                                  }
                                >
                                  Reset
                                </button>
                              </div>
                            ) : discountValidation?.canSelfApprove ? (
                              <div
                                className="status-badge-inline self-approved"
                                title={`Limit: ₹${discountValidation.maxAllowedAmount?.toLocaleString()} (${discountValidation.maxAllowedPercentage}%). Remaining Today: ₹${discountValidation.remainingDailyLimit?.toFixed(0)} | Month: ₹${discountValidation.remainingMonthlyLimit?.toFixed(0)}`}
                              >
                                <span className="badge-text-bold">✓ SELF APPROVED</span>
                                <span className="badge-sub-limit">(Limit: ₹{discountValidation.maxAllowedAmount?.toLocaleString()})</span>
                              </div>
                            ) : (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <span
                                  className="status-badge-inline requires-approval"
                                  title={discountValidation?.validationMessage || 'Discount exceeds self-approval limit.'}
                                >
                                  ⚠️ APPROVAL REQ.
                                </span>
                                <button
                                  type="button"
                                  className="btn-request-discount-mini"
                                  onClick={handleSendDiscountForApproval}
                                  disabled={approvalRequest.submitting}
                                >
                                  Send ↗
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {selectedReasonId === 'CUSTOM' && (
                          <input
                            type="text"
                            style={{ marginTop: '5px' }}
                            className="field-input-compact"
                            placeholder="Specify custom concession reason..."
                            value={discountReason}
                            onChange={(e) => setDiscountReason(e.target.value)}
                          />
                        )}

                        {/* Mandatory Remarks if Reason Requires Remarks or is OTHER */}
                        {(discountReason?.toUpperCase() === 'OTHER' ||
                          selectedReasonId === 'CUSTOM' ||
                          discountReasons.find((r) => String(r.reasonId) === String(selectedReasonId))?.requiresRemarks) && (
                          <div style={{ marginTop: '6px' }}>
                            <label className="field-label-compact req-star">Mandatory Concession Remarks:</label>
                            <input
                              type="text"
                              className="field-input-compact"
                              placeholder="Clinical or administrative justification..."
                              value={discountRemarks}
                              onChange={(e) => setDiscountRemarks(e.target.value)}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* CareCoins Redemption (patient loyalty/rewards) — only shown once a
                      patient is resolved and actually has a redeemable balance; a
                      disabled/never-used rule means every balance is 0, so this stays
                      hidden rather than cluttering the billing flow with an always-zero
                      section (CARECOINS_DESIGN.md fail-closed default). */}
                  {selectedPatientUhid && careCoinsWalletLoading && !careCoinsWallet && (
                    <div className="fin-label-inline" style={{ marginTop: '10px', fontStyle: 'italic', opacity: 0.7 }}>
                      Checking CareCoins balance...
                    </div>
                  )}

                  {selectedPatientUhid && careCoinsWallet?.balancePoints > 0 && (
                    <div className="cashier-financial-sequence" style={{ marginTop: '10px' }}>
                      <div className="fin-single-line-bar">
                        <div className="fin-bar-item fin-bar-gross">
                          <span className="fin-label-inline">CareCoins Balance:</span>
                          <strong className="fin-val-inline">{careCoinsWallet.balancePoints} pts</strong>
                        </div>

                        <div className="fin-bar-separator" />

                        <div className="fin-bar-item fin-bar-amount">
                          <span className="fin-label-inline">Redeem Points:</span>
                          <input
                            type="number"
                            min="0"
                            max={careCoinsWallet.balancePoints}
                            step="1"
                            className="fin-disc-input-compact"
                            value={careCoinsPointsToRedeem}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') {
                                setCareCoinsPointsToRedeem('');
                                return;
                              }
                              const num = parseInt(val, 10);
                              if (isNaN(num) || num < 0) {
                                setCareCoinsPointsToRedeem(0);
                                return;
                              }
                              setCareCoinsPointsToRedeem(Math.min(num, careCoinsWallet.balancePoints));
                            }}
                            placeholder="0"
                            title={`Redeem up to ${careCoinsWallet.balancePoints} CareCoins points`}
                          />
                        </div>

                        <div className="fin-bar-separator" />

                        <div className="fin-bar-item">
                          {isPreviewingCareCoins ? (
                            <span className="status-badge-inline validating">Checking...</span>
                          ) : careCoinsPreviewError ? (
                            <span className="status-badge-inline requires-approval" title={careCoinsPreviewError}>
                              ⚠️ {careCoinsPreviewError}
                            </span>
                          ) : careCoinsPreview?.isEligible ? (
                            <span className="status-badge-inline self-approved">
                              <span className="badge-text-bold">✓ -₹{Number(careCoinsPreview.estimatedDiscountINR).toFixed(2)}</span>
                            </span>
                          ) : (
                            <span className="fin-label-inline" style={{ fontStyle: 'italic', opacity: 0.7 }}>
                              Enter points to preview discount
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sample Collection Charge Calculation & Adjustment Bar */}
                  <CollectionChargePanel
                    centreId={centreId}
                    visitType={visitType}
                    pincode={patientData.pincode}
                    orderValue={Math.max(0, grossAmount - discountAmount)}
                    onChange={setCollectionCharge}
                  />

                  {/* Multi-Payment Mode Workbench (Compact Dropdown + Amount Row) */}
                  {partyId && (
                    <div role="status" style={{ margin: '8px 0', padding: '10px 14px', borderRadius: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a', fontSize: '13px', fontWeight: 600 }}>
                      🏢 This bill goes on credit to the client. Do not collect payment from the patient. The client is invoiced later in B2B Corporate Billing.
                    </div>
                  )}
                  <div className="payment-workbench-container cashier-workbench" style={partyId ? { display: 'none' } : undefined}>
                    <div className="cashier-entry-box">
                      {/* Payment Method Selector & Amount Row */}
                      <div className="cashier-mode-and-amount-grid">
                        <div className="cashier-field-col">
                          <label className="cashier-field-label">Payment Method</label>
                          <select
                            className="field-select text-bold"
                            value={currentPaymentMode}
                            onChange={(e) => setCurrentPaymentMode(e.target.value)}
                          >
                            <option value="">-- Select Mode --</option>
                            {lookups.paymentModes.map((pm) => (
                              <option key={pm.code} value={pm.value}>
                                {pm.value.includes('Cash') ? '💵 Cash' :
                                 pm.value.includes('Card') ? '💳 Card / POS' :
                                 pm.value.includes('Online') ? '🌐 UPI / Online' :
                                 pm.value.includes('Cheque') ? '🏛️ Cheque / DD' : '🏢 Panel Credit'}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="cashier-field-col">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label className="cashier-field-label">
                              Payment Amount (Applied) <span className="req-star">*</span>
                            </label>
                            {remainingBalance > 0 && parseFloat(currentPayAmount || 0) !== remainingBalance && (
                              <button
                                type="button"
                                style={{
                                  fontSize: '11px',
                                  background: '#ecfdf5',
                                  color: '#059669',
                                  border: '1px solid #a7f3d0',
                                  borderRadius: '4px',
                                  padding: '1px 6px',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  marginBottom: '4px'
                                }}
                                onClick={() => {
                                  setCurrentPayAmount(remainingBalance.toFixed(2));
                                  setIsPayAmountManuallyEdited(false);
                                }}
                                title="Reset payment amount to full remaining balance"
                              >
                                Pay Full (₹{remainingBalance.toFixed(2)})
                              </button>
                            )}
                          </div>
                          <div className="cashier-input-with-symbol">
                            <span className="currency-prefix">₹</span>
                            <input
                              type="number"
                              min="0.01"
                              max={remainingBalance}
                              step="0.01"
                              className="cashier-field-input text-bold"
                              placeholder={`Max ₹${remainingBalance.toFixed(2)}`}
                              value={currentPayAmount}
                              onChange={(e) => {
                                setCurrentPayAmount(e.target.value);
                                setIsPayAmountManuallyEdited(true);
                              }}
                            />
                          </div>
                          <span className="cashier-field-sub">Capped at ₹{remainingBalance.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Mode Specific Additional Details (Only shown if mode requires extra info) */}
                      {currentPaymentMode === 'Credit / Debit Card' && (
                        <div className="cashier-extra-fields-row animate-fade-in">
                          <div className="cashier-field-col">
                            <label className="cashier-field-label">Card Last 4 Digits</label>
                            <input
                              type="text"
                              maxLength={4}
                              className="cashier-field-input"
                              placeholder="e.g. 4821"
                              value={cardLast4}
                              onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, ''))}
                            />
                          </div>
                          <div className="cashier-field-col">
                            <label className="cashier-field-label">POS Terminal / Ref ID</label>
                            <input
                              type="text"
                              className="cashier-field-input"
                              placeholder="e.g. HDFC-POS-04 / TXN-98124"
                              value={cardMachineRef}
                              onChange={(e) => setCardMachineRef(e.target.value)}
                            />
                          </div>
                        </div>
                      )}

                      {currentPaymentMode === 'UPI / Online Payment' && (
                        <div className="cashier-extra-fields-row animate-fade-in">
                          <div className="cashier-field-col" style={{ width: '100%' }}>
                            <label className="cashier-field-label">UPI UTR / Reference No</label>
                            <input
                              type="text"
                              className="cashier-field-input"
                              placeholder="12-digit UTR (e.g. 426819001234)"
                              value={upiUtr}
                              onChange={(e) => setUpiUtr(e.target.value)}
                            />
                          </div>
                        </div>
                      )}

                      {currentPaymentMode === 'Cheque / DD' && (
                        <div className="cashier-extra-fields-row cheque-grid animate-fade-in">
                          <div className="cashier-field-col">
                            <label className="cashier-field-label">Cheque / DD No</label>
                            <input
                              type="text"
                              className="cashier-field-input"
                              placeholder="Cheque No"
                              value={chequeNumber}
                              onChange={(e) => setChequeNumber(e.target.value)}
                            />
                          </div>
                          <div className="cashier-field-col">
                            <label className="cashier-field-label">Bank Name</label>
                            <input
                              type="text"
                              className="cashier-field-input"
                              placeholder="e.g. SBI / HDFC"
                              value={chequeBank}
                              onChange={(e) => setChequeBank(e.target.value)}
                            />
                          </div>
                          <div className="cashier-field-col">
                            <label className="cashier-field-label">Cheque Date</label>
                            <input
                              type="date"
                              className="cashier-field-input"
                              value={chequeDateVal}
                              onChange={(e) => setChequeDateVal(e.target.value)}
                            />
                          </div>
                        </div>
                      )}

                      {currentPaymentMode === 'Credit / Panel Sponsor' && (
                        <div className="cashier-extra-fields-row animate-fade-in">
                          <div className="panel-sponsor-badge" style={{ width: '100%' }}>
                            🏢 Corporate Panel Credit: <strong>{lookups.panels.find((p) => p.panelId === panelId)?.panelName || 'Selected Panel'}</strong>
                          </div>
                        </div>
                      )}

                      {/* Add Payment Button */}
                      <div className="cashier-add-btn-wrap">
                        <button
                          type="button"
                          className="btn-cashier-add-split"
                          onClick={handleAddPayment}
                          disabled={
                            !currentPaymentMode ||
                            remainingBalance <= 0 ||
                            !currentPayAmount ||
                            parseFloat(currentPayAmount) <= 0 ||
                            parseFloat(currentPayAmount) > remainingBalance + 0.001
                          }
                        >
                          + Add Payment (₹{parseFloat(currentPayAmount || 0).toFixed(2)})
                        </button>
                      </div>
                    </div>

                    {/* Payment Breakdown History Table */}
                    <div className="cashier-breakdown-wrap">
                      <div className="breakdown-header-bar">
                        <span className="breakdown-title">
                          Applied Payments ({paymentsList.length})
                        </span>
                        <span className="breakdown-running-total">
                          Total Paid: <strong>₹ {totalPaid.toFixed(2)}</strong>
                        </span>
                      </div>

                      {paymentsList.length > 0 ? (
                        <div className="cashier-table-container">
                          <table className="cashier-breakdown-table">
                            <thead>
                              <tr>
                                <th style={{ width: '28px' }}>#</th>
                                <th style={{ width: '100px' }}>Mode</th>
                                <th>Transaction Ref / Details</th>
                                <th style={{ textAlign: 'right', width: '95px' }}>Amount</th>
                                <th style={{ textAlign: 'center', width: '36px' }}>Del</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paymentsList.map((p, idx) => (
                                <tr key={p.id}>
                                  <td className="cell-muted">{idx + 1}</td>
                                  <td>
                                    <span className="cashier-pm-badge">{p.paymentMode}</span>
                                  </td>
                                  <td className="cell-details">{p.reference}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669' }}>
                                    ₹ {p.amount.toFixed(2)}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    <button
                                      type="button"
                                      className="btn-cashier-del"
                                      title="Remove payment entry"
                                      onClick={() => handleRemovePayment(p.id)}
                                    >
                                      🗑️
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="cashier-tfoot-row">
                                <td colSpan={3} style={{ textAlign: 'right', fontWeight: 600 }}>
                                  {remainingBalance > 0 ? 'Remaining Balance Due:' : 'All Payments Applied:'}
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 800, color: remainingBalance > 0 ? '#d97706' : '#059669' }}>
                                  ₹ {remainingBalance.toFixed(2)}
                                </td>
                                <td />
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      ) : (
                        <div className="cashier-no-payments">
                          <span>No payments recorded yet. Full due or panel credit billing is allowed.</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Automated Post-Registration Actions (Config-Driven) */}
                  {effectiveConfig?.autoCollectOnRegistration && (
                    <div className="workflow-auto-box">
                      <div className="workflow-auto-heading">
                        <span>⚙️ Post-Registration Automation</span>
                      </div>
                      <div className="workflow-auto-checks">
                        <label className="workflow-check-item">
                          <input
                            type="checkbox"
                            checked={autoCollectSample}
                            onChange={(e) => {
                              const val = e.target.checked;
                              setAutoCollectSample(val);
                              if (!val) setAutoReceiveSample(false);
                            }}
                          />
                          <span>Collect Sample automatically after Registration</span>
                        </label>
                        {effectiveConfig?.autoReceiveOnRegistration && (
                          <label className={`workflow-check-item ${!autoCollectSample ? 'disabled' : ''}`}>
                            <input
                              type="checkbox"
                              checked={autoReceiveSample}
                              disabled={!autoCollectSample}
                              onChange={(e) => setAutoReceiveSample(e.target.checked)}
                            />
                            <span>Receive Sample in Lab automatically after Registration</span>
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* ===================================================================== */}
      {/* 9. STICKY BOTTOM ACTION BAR (PINNED ACROSS BOTH TABS)                 */}
      {/* ===================================================================== */}
      <div className="sticky-action-dock">
        <div className="action-dock-summary">
          <div className="dock-stat-chip">
            <span className="dock-stat-label">Tests</span>
            <strong className="dock-stat-val">{selectedTests.length}</strong>
          </div>
          {Number(collectionCharge?.amount) > 0 && (
            <div className="dock-stat-chip">
              <span className="dock-stat-label">Collection Charge</span>
              <strong className="dock-stat-val">₹ {Number(collectionCharge.amount).toFixed(2)}</strong>
            </div>
          )}
          <div className="dock-stat-chip">
            <span className="dock-stat-label">Net Payable</span>
            <strong className="dock-stat-val text-accent">₹ {netAmount.toFixed(2)}</strong>
          </div>
          <div className="dock-stat-chip">
            <span className="dock-stat-label">Paid</span>
            <strong className="dock-stat-val text-success">₹ {totalPaid.toFixed(2)}</strong>
          </div>
          <div className="dock-stat-chip">
            <span className="dock-stat-label">Due</span>
            <strong className={`dock-stat-val ${remainingBalance > 0 ? 'text-warning' : 'text-success'}`}>
              ₹ {remainingBalance.toFixed(2)}
            </strong>
          </div>
          {priority === 'Urgent' && (
            <div className="dock-stat-chip dock-stat-urgent">
              <strong className="dock-stat-val text-urgent">🔴 URGENT STAT</strong>
            </div>
          )}
          <div className="dock-stat-chip">
            <span className="dock-stat-label">Status</span>
            <strong className={`dock-stat-val ${isFormReady ? 'text-success' : 'text-warning'}`}>
              {isFormReady ? 'READY' : 'INCOMPLETE'}
            </strong>
          </div>
          {patientData.pName && (
            <div className="dock-patient-snippet">
              <span>Patient:</span>
              <strong>{patientData.pName}</strong>
            </div>
          )}
        </div>

        <div className="action-dock-buttons">
          <button
            type="button"
            className="dock-btn btn-reset"
            onClick={handleResetToNewPatient}
            disabled={submitting}
            title="Clear all fields, test cart, and payment entries"
          >
            🔄 Reset
          </button>
          
          <button
            type="button"
            className="dock-btn btn-warning"
            onClick={handleSaveDraft}
            disabled={submitting}
            style={{ backgroundColor: '#f59e0b', color: 'white', border: 'none' }}
            title="Put session on hold and save to drafts"
          >
            ⏸ Hold
          </button>
          
          <button
            type="button"
            className="dock-btn btn-secondary"
            onClick={() => setIsDraftsModalOpen(true)}
            disabled={submitting}
            style={{ position: 'relative' }}
          >
            📂 Drafts
            {drafts.length > 0 && (
              <span style={{
                position: 'absolute', top: '-8px', right: '-8px', 
                background: '#ef4444', color: 'white', 
                borderRadius: '50%', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold'
              }}>
                {drafts.length}
              </span>
            )}
          </button>
          <button
            type="button"
            className="dock-btn btn-primary-complete"
            onClick={handleRegister}
            disabled={!isFormReady || submitting}
            style={submitting ? { opacity: 0.65, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
            title={!isFormReady ? missingRequirementsText : 'Finalize complete registration and generate receipt'}
          >
            ✨ {submitting ? 'Registering... Please wait' : 'Register'}
          </button>
        </div>
      </div>

      {/* ===================================================================== */}
      {/* DRAFTS MODAL                                                          */}
      {/* ===================================================================== */}
      {isDraftsModalOpen && (
        <div className="futuristic-modal-backdrop" onClick={() => setIsDraftsModalOpen(false)}>
          <div className="futuristic-modal-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header-bar">
              <div className="modal-title-wrap">
                <span className="modal-icon">📂</span>
                <h3 className="modal-title">Saved Drafts</h3>
              </div>
              <div className="modal-header-actions">
                <button type="button" className="btn-close-modal" onClick={() => setIsDraftsModalOpen(false)}>×</button>
              </div>
            </div>
            <div className="modal-body">
              {drafts.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#64748b', padding: '30px' }}>
                  No drafted sessions.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {drafts.map((draft) => (
                    <div key={draft.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc' }}>
                      <div>
                        <strong style={{ display: 'block', color: '#0f172a' }}>{draft.data?.patientData?.pName || 'Unknown Patient'}</strong>
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          {new Date(draft.timestamp).toLocaleString()} • {draft.data?.selectedTests?.length || 0} Tests
                        </span>
                        {draft.data?.approvalRequest?.status === 'PENDING' && (
                          <span style={{ display: 'block', fontSize: '0.75rem', color: '#b45309', marginTop: '4px', background: '#fef3c7', padding: '2px 6px', borderRadius: '4px', width: 'fit-content' }}>
                            ⏳ Pending Discount Approval
                          </span>
                        )}
                        {draft.data?.approvalRequest?.status === 'APPROVED' && (
                          <span style={{ display: 'block', fontSize: '0.75rem', color: '#15803d', marginTop: '4px', background: '#dcfce3', padding: '2px 6px', borderRadius: '4px', width: 'fit-content' }}>
                            ✓ Discount Approved
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="button" className="btn-sm btn-primary" onClick={() => handleResumeDraft(draft)}>Resume</button>
                        <button type="button" className="btn-sm btn-danger" onClick={() => handleDeleteDraft(draft.id)} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '4px', padding: '4px 8px' }}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* DOCUMENT PREVIEW MODAL                                                */}
      {/* ===================================================================== */}
      {previewDoc && (
        <div className="futuristic-modal-backdrop" onClick={() => setPreviewDoc(null)}>
          <div className="futuristic-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-bar">
              <div className="modal-title-wrap">
                <span className="modal-icon">📄</span>
                <h3 className="modal-title">{previewDoc.documentName}</h3>
                <span className="modal-badge">{previewDoc.documentType}</span>
              </div>
              <div className="modal-header-actions">
                <a
                  href={patientDocumentService.getDocumentDownloadUrl(previewDoc.documentId)}
                  download
                  className="modal-action-btn-download"
                  title="Download Document"
                >
                  ⬇️ Download
                </a>
                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={() => setPreviewDoc(null)}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="modal-body-viewport">
              {previewDoc.mimeType === 'application/pdf' ? (
                <iframe
                  src={patientDocumentService.getDocumentViewUrl(previewDoc.documentId)}
                  title={previewDoc.documentName}
                  className="modal-preview-iframe"
                />
              ) : previewDoc.mimeType && previewDoc.mimeType.startsWith('image/') ? (
                <img
                  src={patientDocumentService.getDocumentViewUrl(previewDoc.documentId)}
                  alt={previewDoc.documentName}
                  className="modal-preview-img"
                />
              ) : (
                <div className="modal-preview-fallback">
                  <span className="fallback-icon">📁</span>
                  <h4>In-Browser Preview Not Supported</h4>
                  <p>This file type cannot be displayed directly in the browser viewer.</p>
                  <a
                    href={patientDocumentService.getDocumentDownloadUrl(previewDoc.documentId)}
                    download
                    className="btn-download-file-large"
                  >
                    ⬇️ Download {previewDoc.documentName}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* RECEIPT MODAL                                                         */}
      {/* ===================================================================== */}
      <ReceiptModal
        isOpen={isReceiptModalOpen}
        onClose={() => {
          setIsReceiptModalOpen(false);
          handleResetToNewPatient();
        }}
        receipt={receiptData}
      />

      {/* ===================================================================== */}
      {/* LIVE WEBCAM PATIENT PHOTO CAPTURE MODAL                               */}
      {/* ===================================================================== */}
      <PatientCameraModal
        isOpen={isCameraModalOpen}
        onClose={() => setIsCameraModalOpen(false)}
        onPhotoCaptured={handlePhotoFromCamera}
        onSwitchToUpload={() => photoFileInputRef.current?.click()}
      />

      {/* ===================================================================== */}
      {/* PATIENT FACIAL BIOMETRIC IDENTIFICATION MODAL                         */}
      {/* ===================================================================== */}
      <FaceBiometricModal
        isOpen={isFaceScanModalOpen}
        onClose={() => {
          setIsFaceScanModalOpen(false);
          if (faceIdStatus === 'idle') {
            setFaceIdStatus('cancelled');
            setFaceIdStatusDetail('Face identification cancelled');
          }
        }}
        mode="identify_patient"
        onSuccess={handlePatientFaceScanSuccess}
        onFallback={() => {
          setIsFaceScanModalOpen(false);
          setFaceIdStatus('unmatched');
          setFaceIdStatusDetail('Face identification skipped. Manual check available.');
          if (patientSearchInputRef.current) {
            patientSearchInputRef.current.focus();
          }
        }}
      />
    </div>
  );
};

export default RegistrationPage;
